/** XeAgents.ai**/
import { useCallback, useState, useEffect, useRef, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  ConnectionMode,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  type EdgeTypes,
  type NodeChange,
  type EdgeChange,
  type EdgeProps,
  BackgroundVariant,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Save,
  Play,
  MessageSquare,
  ShieldAlert,
  HelpCircle,
  GitBranch,
  Calendar,
  FileText,
  Webhook,
  Phone,
  PhoneForwarded,
  Clock,
  StopCircle,
  Plus,
  ArrowLeft,
  X,
  Trash2,
  Info,
  Wand2,
  Loader2,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Undo2,
  Redo2,
  Camera,
  Upload,
  Paperclip,
  Send,
  Sparkles,
  Image,
  Zap,
  ArrowUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useRoute, useLocation } from "wouter";
import { TestFlowDialog } from "@/components/TestFlowDialog";
import { FlowBuilderHelpDrawer } from "@/components/flow-automation/FlowBuilderHelpDrawer";
import { FlowScriptPanel } from "@/components/flow-automation/FlowScriptPanel";
import { PlaceholderHelper, PlaceholderHint } from "@/components/flow-automation/PlaceholderHelper";
import { deduplicateFlowEdges, addEdgeWithDeduplication } from "@/utils/flow-validation";

// Flow node data interface
// Response option for conditional branching on question nodes
interface ResponseOption {
  id: string;
  label: string; // Display label (e.g., "Yes", "No", "Maybe")
  keywords: string[]; // Keywords to match in response (e.g., ["yes", "sure", "okay"])
  color?: string; // Optional color for the handle
}

interface FlowNodeConfig {
  type?: string;
  message?: string;
  waitForResponse?: boolean;
  question?: string;
  variableName?: string;
  condition?: string;
  trueBranch?: string;
  falseBranch?: string;
  phoneNumber?: string;
  duration?: number;
  webhookUrl?: string;
  formId?: string;
  appointmentType?: string;
  hostEmail?: string; // Calendar host email for appointment booking
  attendeeEmails?: string[]; // Additional attendee emails for appointment booking
  endMessage?: string;
  // Response options for conditional branching on question nodes
  responseOptions?: ResponseOption[];
  enableBranching?: boolean; // Whether to show branching handles on question node
}

interface FlowNodeData extends Record<string, unknown> {
  type: string;
  label: string;
  config?: FlowNodeConfig;
}

type FlowNode = Node<FlowNodeData>;
type FlowEdge = Edge;

// Icon mapping
const nodeTypeIcons = {
  message: MessageSquare,
  recordingConsent: ShieldAlert, // GDPR/Compliance: Recording disclosure
  question: HelpCircle,
  condition: GitBranch,
  multiCondition: GitBranch, // Permission check, objection handling
  intentRouter: GitBranch, // Alias for multiCondition
  appointment: Calendar,
  form: FileText,
  webhook: Webhook,
  transfer: Phone,
  delay: Clock,
  end: StopCircle,
  callback: Phone, // Callback scheduling
  callOutcome: StopCircle, // Call outcome tracking
  doNotCall: StopCircle, // DNC handling
  referralCapture: MessageSquare, // Referral capture
  voicemail: Phone, // Voicemail handling
  // Gatekeeper handling nodes
  gatekeeperDetection: PhoneForwarded, // Detect who answered (DM, gatekeeper, voicemail, wrong number)
  gatekeeperBypass: PhoneForwarded, // Follow-up after gatekeeper was reached
  // State machine nodes
  subFlowInvoke: GitBranch, // Sub-flow invocation (reusable flows)
  stateTransition: GitBranch, // State machine transition
  persistVariable: FileText, // Variable persistence
  exitOutcome: StopCircle, // Sub-flow exit point
  stateMachine: GitBranch, // Consolidated state machine node
};

// Context for click-based connections
interface PendingConnection {
  nodeId: string;
  handleId?: string;
  handleType: 'source' | 'target';
}

interface ConnectionContextType {
  pendingConnection: PendingConnection | null;
  setPendingConnection: (conn: PendingConnection | null) => void;
  onHandleClick: (nodeId: string, handleType: 'source' | 'target', handleId?: string) => void;
}

const ConnectionContext = createContext<ConnectionContextType>({
  pendingConnection: null,
  setPendingConnection: () => {},
  onHandleClick: () => {},
});

// Colorful theme for each node type
const nodeTypeColors = {
  message: {
    bg: "from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700",
    icon: "text-white",
    text: "text-white",
    handle: "#3b82f6",
  },
  recordingConsent: {
    bg: "from-rose-500 to-rose-600 dark:from-rose-600 dark:to-rose-700",
    icon: "text-white",
    text: "text-white",
    handle: "#f43f5e",
  },
  question: {
    bg: "from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700",
    icon: "text-white",
    text: "text-white",
    handle: "#a855f7",
  },
  condition: {
    bg: "from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700",
    icon: "text-white",
    text: "text-white",
    handle: "#f59e0b",
  },
  multiCondition: {
    bg: "from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700",
    icon: "text-white",
    text: "text-white",
    handle: "#6366f1",
  },
  intentRouter: {
    bg: "from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700",
    icon: "text-white",
    text: "text-white",
    handle: "#6366f1",
  },
  appointment: {
    bg: "from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700",
    icon: "text-white",
    text: "text-white",
    handle: "#10b981",
  },
  form: {
    bg: "from-cyan-500 to-cyan-600 dark:from-cyan-600 dark:to-cyan-700",
    icon: "text-white",
    text: "text-white",
    handle: "#06b6d4",
  },
  callback: {
    bg: "from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700",
    icon: "text-white",
    text: "text-white",
    handle: "#f59e0b",
  },
  callOutcome: {
    bg: "from-slate-500 to-slate-600 dark:from-slate-600 dark:to-slate-700",
    icon: "text-white",
    text: "text-white",
    handle: "#64748b",
  },
  doNotCall: {
    bg: "from-red-600 to-red-700 dark:from-red-700 dark:to-red-800",
    icon: "text-white",
    text: "text-white",
    handle: "#dc2626",
  },
  referralCapture: {
    bg: "from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700",
    icon: "text-white",
    text: "text-white",
    handle: "#14b8a6",
  },
  voicemail: {
    bg: "from-gray-500 to-gray-600 dark:from-gray-600 dark:to-gray-700",
    icon: "text-white",
    text: "text-white",
    handle: "#6b7280",
  },
  webhook: {
    bg: "from-violet-500 to-violet-600 dark:from-violet-600 dark:to-violet-700",
    icon: "text-white",
    text: "text-white",
    handle: "#8b5cf6",
  },
  transfer: {
    bg: "from-pink-500 to-pink-600 dark:from-pink-600 dark:to-pink-700",
    icon: "text-white",
    text: "text-white",
    handle: "#ec4899",
  },
  delay: {
    bg: "from-orange-500 to-orange-600 dark:from-orange-600 dark:to-orange-700",
    icon: "text-white",
    text: "text-white",
    handle: "#f97316",
  },
  end: {
    bg: "from-red-500 to-red-600 dark:from-red-600 dark:to-red-700",
    icon: "text-white",
    text: "text-white",
    handle: "#ef4444",
  },
  // Gatekeeper handling node colors
  gatekeeperDetection: {
    bg: "from-cyan-500 to-cyan-600 dark:from-cyan-600 dark:to-cyan-700",
    icon: "text-white",
    text: "text-white",
    handle: "#06b6d4",
  },
  gatekeeperBypass: {
    bg: "from-cyan-600 to-cyan-700 dark:from-cyan-700 dark:to-cyan-800",
    icon: "text-white",
    text: "text-white",
    handle: "#0891b2",
  },
  // State machine node colors
  subFlowInvoke: {
    bg: "from-fuchsia-500 to-fuchsia-600 dark:from-fuchsia-600 dark:to-fuchsia-700",
    icon: "text-white",
    text: "text-white",
    handle: "#d946ef",
  },
  stateTransition: {
    bg: "from-sky-500 to-sky-600 dark:from-sky-600 dark:to-sky-700",
    icon: "text-white",
    text: "text-white",
    handle: "#0ea5e9",
  },
  persistVariable: {
    bg: "from-lime-500 to-lime-600 dark:from-lime-600 dark:to-lime-700",
    icon: "text-white",
    text: "text-white",
    handle: "#84cc16",
  },
  exitOutcome: {
    bg: "from-rose-500 to-rose-600 dark:from-rose-600 dark:to-rose-700",
    icon: "text-white",
    text: "text-white",
    handle: "#f43f5e",
  },
  stateMachine: {
    bg: "from-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 dark:from-violet-600 dark:via-purple-600 dark:to-fuchsia-600",
    icon: "text-white",
    text: "text-white",
    handle: "#8b5cf6",
  },
};

// Custom node component with connection handles
function FlowNode({ data, selected, id }: { data: any; selected?: boolean; id: string }) {
  // Debug: Log when FlowNode renders
  console.log('[FlowNode] Rendering node:', id, 'type:', data?.type, 'selected:', selected);

  const Icon =
    nodeTypeIcons[data?.type as keyof typeof nodeTypeIcons] || MessageSquare;
  const colors =
    nodeTypeColors[data?.type as keyof typeof nodeTypeColors] ||
    nodeTypeColors.message;
  const hasMultipleOutputs = data?.type === "condition";
  const isMultiCondition = data?.type === "multiCondition" || data?.type === "intentRouter";
  const intents = isMultiCondition ? (data?.config?.intents || []) : [];

  // Gatekeeper detection nodes always have 4 fixed handles
  const isGatekeeperNode = data?.type === "gatekeeperDetection" || data?.type === "gatekeeperBypass";
  const gatekeeperHandles = isGatekeeperNode ? [
    { name: 'dm_answered', label: 'DM Answered', color: '#22c55e' }, // green
    { name: 'gatekeeper_answered', label: 'Gatekeeper', color: '#f59e0b' }, // amber
    { name: 'voicemail_detected', label: 'Voicemail', color: '#8b5cf6' }, // purple
    { name: 'wrong_number', label: 'Wrong #', color: '#ef4444' }, // red
  ] : [];

  // Question nodes with branching enabled
  const isQuestionWithBranching = data?.type === "question" && data?.config?.enableBranching;
  const responseOptions: ResponseOption[] = isQuestionWithBranching ? (data?.config?.responseOptions || []) : [];

  // State machine nodes have dynamic exit point handles
  const isStateMachineNode = data?.type === "stateMachine";
  const stateMachineExitPoints = isStateMachineNode ? (data?.config?.exitPoints || []).map((ep: any) => ({
    name: ep.id,
    label: ep.name,
    color: ep.color || '#8b5cf6',
  })) : [];

  const { pendingConnection, onHandleClick } = useContext(ConnectionContext);

  // Check if this handle is the pending source
  const isSourcePending = (handleId?: string) =>
    pendingConnection?.nodeId === id &&
    pendingConnection?.handleType === 'source' &&
    pendingConnection?.handleId === handleId;

  const isTargetPending = pendingConnection?.nodeId === id && pendingConnection?.handleType === 'target';

  // Check if we're waiting for a target (source already selected)
  const waitingForTarget = pendingConnection?.handleType === 'source';

  return (
    <div
      className="relative group"
      onClick={() => console.log('[FlowNode] Node wrapper clicked:', id)}
    >
      {/* Input Handle (top) - Click or drag to connect */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 -top-3 z-10 cursor-pointer transition-all duration-200 group/handle ${
          isTargetPending ? 'scale-150' :
          waitingForTarget ? 'hover:scale-150 animate-pulse' : 'hover:scale-125'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onHandleClick(id, 'target');
        }}
        title="Click or drag to connect"
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={true}
          style={{
            position: 'relative',
            transform: 'none',
            background: isTargetPending ? '#14b8a6' : colors.handle,
            width: 22,
            height: 22,
            border: isTargetPending ? '4px solid #14b8a6' : '3px solid white',
            boxShadow: waitingForTarget
              ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
              : '0 2px 8px rgba(0,0,0,0.2)',
            cursor: 'crosshair',
          }}
        />
        {/* Connection hint label */}
        {waitingForTarget && (
          <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-teal-500 text-white px-2 py-0.5 rounded whitespace-nowrap shadow-lg">
            Click here
          </span>
        )}
      </div>

      <div
        className={`bg-gradient-to-br ${colors.bg} rounded-lg p-3 min-w-[220px] shadow-lg ${selected ? "ring-2 ring-white dark:ring-gray-300 ring-offset-2 ring-offset-background" : ""} transition-all hover:shadow-xl cursor-pointer`}
      >
        <div className="flex items-center gap-2.5">
          <Icon className={`w-5 h-5 ${colors.icon} flex-shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-sm ${colors.text}`}>
              {data?.label || 'Node'}
            </div>
            {data?.config?.message && (
              <div className="text-xs text-white/80 mt-1 truncate">
                {data.config.message.substring(0, 50)}...
              </div>
            )}
            {data?.config?.question && (
              <div className="text-xs text-white/80 mt-1 truncate">
                {data.config.question.substring(0, 50)}...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output Handle (bottom) - Click or drag to connect */}
      {hasMultipleOutputs ? (
        <>
          {/* True branch - Green */}
          <div
            className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
              isSourcePending('true') ? 'scale-150' : 'hover:scale-150'
            }`}
            style={{ left: '30%', transform: 'translateX(-50%)' }}
            onClick={(e) => {
              e.stopPropagation();
              onHandleClick(id, 'source', 'true');
            }}
            title="Click or drag to connect (Yes branch)"
          >
            <Handle
              type="source"
              position={Position.Bottom}
              id="true"
              isConnectable={true}
              style={{
                position: 'relative',
                transform: 'none',
                left: 0,
                background: isSourcePending('true') ? '#14b8a6' : '#22c55e',
                width: 22,
                height: 22,
                border: isSourcePending('true') ? '4px solid #14b8a6' : '3px solid white',
                boxShadow: isSourcePending('true')
                  ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                  : '0 2px 8px rgba(0,0,0,0.2)',
                cursor: 'crosshair',
              }}
            />
          </div>
          {/* False branch - Red */}
          <div
            className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
              isSourcePending('false') ? 'scale-150' : 'hover:scale-150'
            }`}
            style={{ left: '70%', transform: 'translateX(-50%)' }}
            onClick={(e) => {
              e.stopPropagation();
              onHandleClick(id, 'source', 'false');
            }}
            title="Click or drag to connect (No branch)"
          >
            <Handle
              type="source"
              position={Position.Bottom}
              id="false"
              isConnectable={true}
              style={{
                position: 'relative',
                transform: 'none',
                left: 0,
                background: isSourcePending('false') ? '#14b8a6' : '#ef4444',
                width: 22,
                height: 22,
                border: isSourcePending('false') ? '4px solid #14b8a6' : '3px solid white',
                boxShadow: isSourcePending('false')
                  ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                  : '0 2px 8px rgba(0,0,0,0.2)',
                cursor: 'crosshair',
              }}
            />
          </div>
          {/* Labels for condition branches */}
          <div className="absolute -bottom-8 left-[30%] -translate-x-1/2 text-[10px] font-semibold text-green-600 bg-green-50 dark:bg-green-950 px-1.5 py-0.5 rounded shadow">Yes</div>
          <div className="absolute -bottom-8 left-[70%] -translate-x-1/2 text-[10px] font-semibold text-red-500 bg-red-50 dark:bg-red-950 px-1.5 py-0.5 rounded shadow">No</div>
        </>
      ) : isMultiCondition && intents.length > 0 ? (
        <>
          {/* MultiCondition: Render a handle for each intent */}
          {intents.map((intent: { name: string; description?: string }, index: number) => {
            const totalIntents = intents.length;
            // Calculate horizontal position: evenly distribute handles across the node width
            const positionPercent = totalIntents === 1 ? 50 : (15 + (index * (70 / (totalIntents - 1))));
            // Color palette for different intents
            const intentColors: Record<string, string> = {
              interested: '#22c55e', // green
              continue: '#22c55e', // green
              yes: '#22c55e', // green
              busy: '#f59e0b', // amber
              callback: '#f59e0b', // amber
              not_interested: '#ef4444', // red
              notinterested: '#ef4444', // red
              no: '#ef4444', // red
              do_not_call: '#dc2626', // dark red
              donotcall: '#dc2626', // dark red
              dnc: '#dc2626', // dark red
            };
            const intentColor = intentColors[intent.name.toLowerCase()] || colors.handle;

            return (
              <div key={intent.name}>
                <div
                  className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
                    isSourcePending(intent.name) ? 'scale-150' : 'hover:scale-150'
                  }`}
                  style={{ left: `${positionPercent}%`, transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHandleClick(id, 'source', intent.name);
                  }}
                  title={`Click to connect: ${intent.description || intent.name}`}
                >
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={intent.name}
                    isConnectable={true}
                    style={{
                      position: 'relative',
                      transform: 'none',
                      left: 0,
                      background: isSourcePending(intent.name) ? '#14b8a6' : intentColor,
                      width: 18,
                      height: 18,
                      border: isSourcePending(intent.name) ? '4px solid #14b8a6' : '2px solid white',
                      boxShadow: isSourcePending(intent.name)
                        ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                        : '0 2px 6px rgba(0,0,0,0.2)',
                      cursor: 'crosshair',
                    }}
                  />
                </div>
                {/* Label for intent */}
                <div
                  className="absolute -bottom-8 text-[8px] font-medium px-1 py-0.5 rounded shadow whitespace-nowrap"
                  style={{
                    left: `${positionPercent}%`,
                    transform: 'translateX(-50%)',
                    backgroundColor: `${intentColor}20`,
                    color: intentColor,
                  }}
                >
                  {intent.name.replace(/_/g, ' ')}
                </div>
              </div>
            );
          })}
        </>
      ) : isQuestionWithBranching && responseOptions.length > 0 ? (
        <>
          {/* Question with branching: Render handles for each response option */}
          {responseOptions.map((option: ResponseOption, index: number) => {
            const totalOptions = responseOptions.length;
            // Calculate horizontal position: evenly distribute handles
            const positionPercent = totalOptions === 1 ? 50 : (15 + (index * (70 / (totalOptions - 1))));
            // Color coding for response types
            const responseColors: Record<string, string> = {
              yes: '#22c55e', // green
              positive: '#22c55e',
              interested: '#22c55e',
              confirm: '#22c55e',
              no: '#ef4444', // red
              negative: '#ef4444',
              not_interested: '#ef4444',
              decline: '#ef4444',
              maybe: '#f59e0b', // amber
              later: '#f59e0b',
              callback: '#f59e0b',
              unsure: '#f59e0b',
            };
            const optionColor = option.color || responseColors[option.label.toLowerCase()] || colors.handle;

            return (
              <div key={option.id}>
                <div
                  className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
                    isSourcePending(option.id) ? 'scale-150' : 'hover:scale-150'
                  }`}
                  style={{ left: `${positionPercent}%`, transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHandleClick(id, 'source', option.id);
                  }}
                  title={`Click to connect: ${option.label}${option.keywords?.length ? ` (matches: ${option.keywords.join(', ')})` : ''}`}
                >
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={option.id}
                    isConnectable={true}
                    style={{
                      position: 'relative',
                      transform: 'none',
                      left: 0,
                      background: isSourcePending(option.id) ? '#14b8a6' : optionColor,
                      width: 18,
                      height: 18,
                      border: isSourcePending(option.id) ? '4px solid #14b8a6' : '2px solid white',
                      boxShadow: isSourcePending(option.id)
                        ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                        : '0 2px 6px rgba(0,0,0,0.2)',
                      cursor: 'crosshair',
                    }}
                  />
                </div>
                {/* Label for response option */}
                <div
                  className="absolute -bottom-8 text-[8px] font-medium px-1 py-0.5 rounded shadow whitespace-nowrap"
                  style={{
                    left: `${positionPercent}%`,
                    transform: 'translateX(-50%)',
                    backgroundColor: `${optionColor}20`,
                    color: optionColor,
                  }}
                >
                  {option.label}
                </div>
              </div>
            );
          })}
        </>
      ) : isGatekeeperNode ? (
        <>
          {/* Gatekeeper Detection: Always render 4 fixed handles */}
          {gatekeeperHandles.map((handle, index) => {
            const totalHandles = gatekeeperHandles.length;
            // Calculate horizontal position: evenly distribute 4 handles
            const positionPercent = 10 + (index * (80 / (totalHandles - 1)));

            return (
              <div key={handle.name}>
                <div
                  className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
                    isSourcePending(handle.name) ? 'scale-150' : 'hover:scale-150'
                  }`}
                  style={{ left: `${positionPercent}%`, transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHandleClick(id, 'source', handle.name);
                  }}
                  title={`Click to connect: ${handle.label}`}
                >
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={handle.name}
                    isConnectable={true}
                    style={{
                      position: 'relative',
                      transform: 'none',
                      left: 0,
                      background: isSourcePending(handle.name) ? '#14b8a6' : handle.color,
                      width: 18,
                      height: 18,
                      border: isSourcePending(handle.name) ? '4px solid #14b8a6' : '2px solid white',
                      boxShadow: isSourcePending(handle.name)
                        ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                        : '0 2px 6px rgba(0,0,0,0.2)',
                      cursor: 'crosshair',
                    }}
                  />
                </div>
                {/* Label for gatekeeper handle */}
                <div
                  className="absolute -bottom-8 text-[8px] font-medium px-1 py-0.5 rounded shadow whitespace-nowrap"
                  style={{
                    left: `${positionPercent}%`,
                    transform: 'translateX(-50%)',
                    backgroundColor: `${handle.color}20`,
                    color: handle.color,
                  }}
                >
                  {handle.label}
                </div>
              </div>
            );
          })}
        </>
      ) : isStateMachineNode && stateMachineExitPoints.length > 0 ? (
        <>
          {/* State Machine: Render dynamic exit point handles */}
          {stateMachineExitPoints.map((exitPoint: { name: string; label: string; color: string }, index: number) => {
            const totalExits = stateMachineExitPoints.length;
            // Calculate horizontal position: evenly distribute handles
            const positionPercent = totalExits === 1 ? 50 : 10 + (index * (80 / (totalExits - 1)));

            return (
              <div key={exitPoint.name}>
                <div
                  className={`absolute -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
                    isSourcePending(exitPoint.name) ? 'scale-150' : 'hover:scale-150'
                  }`}
                  style={{ left: `${positionPercent}%`, transform: 'translateX(-50%)' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onHandleClick(id, 'source', exitPoint.name);
                  }}
                  title={`Click to connect: ${exitPoint.label}`}
                >
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id={exitPoint.name}
                    isConnectable={true}
                    style={{
                      position: 'relative',
                      transform: 'none',
                      left: 0,
                      background: isSourcePending(exitPoint.name) ? '#14b8a6' : exitPoint.color,
                      width: 18,
                      height: 18,
                      border: isSourcePending(exitPoint.name) ? '4px solid #14b8a6' : '2px solid white',
                      boxShadow: isSourcePending(exitPoint.name)
                        ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                        : '0 2px 6px rgba(0,0,0,0.2)',
                      cursor: 'crosshair',
                    }}
                  />
                </div>
                {/* Label for exit point */}
                <div
                  className="absolute -bottom-8 text-[8px] font-medium px-1 py-0.5 rounded shadow whitespace-nowrap"
                  style={{
                    left: `${positionPercent}%`,
                    transform: 'translateX(-50%)',
                    backgroundColor: `${exitPoint.color}20`,
                    color: exitPoint.color,
                  }}
                >
                  {exitPoint.label}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div
          className={`absolute left-1/2 -translate-x-1/2 -bottom-3 z-10 cursor-pointer transition-all duration-200 ${
            isSourcePending(undefined) ? 'scale-150' : 'hover:scale-150'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onHandleClick(id, 'source');
          }}
          title="Click or drag to connect"
        >
          <Handle
            type="source"
            position={Position.Bottom}
            isConnectable={true}
            style={{
              position: 'relative',
              transform: 'none',
              background: isSourcePending(undefined) ? '#14b8a6' : colors.handle,
              width: 22,
              height: 22,
              border: isSourcePending(undefined) ? '4px solid #14b8a6' : '3px solid white',
              boxShadow: isSourcePending(undefined)
                ? '0 0 15px rgba(20, 184, 166, 0.9), 0 0 30px rgba(20, 184, 166, 0.5)'
                : '0 2px 8px rgba(0,0,0,0.2)',
              cursor: 'crosshair',
            }}
          />
        </div>
      )}
    </div>
  );
}

// Custom edge component with labels for conditional branches
function LabeledEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
  style,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Get label from edge data or sourceHandle
  const label = data?.label || data?.sourceHandle || '';

  // Determine color based on label content
  const getLabelColor = (lbl: string) => {
    const lower = lbl.toLowerCase();
    if (['yes', 'true', 'interested', 'confirm', 'positive'].some(k => lower.includes(k))) {
      return { bg: 'rgba(34, 197, 94, 0.95)', text: 'white' }; // green
    }
    if (['no', 'false', 'not_interested', 'decline', 'negative'].some(k => lower.includes(k))) {
      return { bg: 'rgba(239, 68, 68, 0.95)', text: 'white' }; // red
    }
    if (['maybe', 'later', 'callback', 'unsure'].some(k => lower.includes(k))) {
      return { bg: 'rgba(245, 158, 11, 0.95)', text: 'white' }; // amber
    }
    return { bg: 'rgba(20, 184, 166, 0.95)', text: 'white' }; // teal default
  };

  const colors = label ? getLabelColor(label) : { bg: 'transparent', text: 'transparent' };

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeWidth: 3,
          stroke: "url(#edge-gradient)",
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <div
              style={{
                background: colors.bg,
                color: colors.text,
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: 600,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                whiteSpace: 'nowrap',
              }}
            >
              {label.replace(/_/g, ' ')}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const edgeTypes: EdgeTypes = {
  labeled: LabeledEdge,
};

const nodeTypes: NodeTypes = {
  message: FlowNode,
  recordingConsent: FlowNode, // GDPR/Compliance: Recording disclosure after greeting
  question: FlowNode,
  condition: FlowNode,
  multiCondition: FlowNode, // For permission check, objection handling with multiple intents
  intentRouter: FlowNode, // Alias for multiCondition
  appointment: FlowNode,
  form: FlowNode,
  webhook: FlowNode,
  transfer: FlowNode,
  delay: FlowNode,
  end: FlowNode,
  custom: FlowNode,
  callback: FlowNode, // For callback scheduling nodes
  callOutcome: FlowNode, // For call outcome tracking
  doNotCall: FlowNode, // For DNC handling
  referralCapture: FlowNode, // For referral capture
  voicemail: FlowNode, // For voicemail handling
  gatekeeperDetection: FlowNode, // For detecting who answered (DM, gatekeeper, voicemail, wrong number)
  gatekeeperBypass: FlowNode, // For follow-up after gatekeeper was reached
  flowNode: FlowNode, // For nodes generated by AI flow generator
  stateMachine: FlowNode, // State machine node for consolidated conversation flows
};

export default function FlowBuilderPage() {
  const { t } = useTranslation();
  const [, params] = useRoute("/app/flows/:id");
  const [, setLocation] = useLocation();
  const flowId = params?.id;

  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
  const [flowName, setFlowName] = useState(t("flows.flowNamePlaceholder"));
  const [flowDescription, setFlowDescription] = useState("");
  const [agentId, setAgentId] = useState<string>("");
  const [firstMessage, setFirstMessage] = useState<string>("");
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [scriptOpen, setScriptOpen] = useState(false);

  // Debug: Track selectedNode changes
  useEffect(() => {
    console.log('[FlowBuilder] selectedNode changed:', selectedNode?.id, selectedNode?.data?.type);
  }, [selectedNode]);

  // Debug: Track nodes state changes
  useEffect(() => {
    console.log('[FlowBuilder] nodes state updated, count:', nodes.length);
    if (nodes.length > 0) {
      console.log('[FlowBuilder] First node full details:', JSON.stringify({
        id: nodes[0].id,
        type: nodes[0].type,
        dataType: nodes[0].data?.type,
        position: nodes[0].position,
        draggable: nodes[0].draggable,
        selectable: nodes[0].selectable,
        selected: nodes[0].selected,
      }));
      console.log('[FlowBuilder] All node types:', nodes.map(n => `${n.id}:${n.type}`).join(', '));
    }
  }, [nodes]);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [generatingField, setGeneratingField] = useState<string | null>(null);
  const [pendingConnection, setPendingConnection] = useState<PendingConnection | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGeneratingNodes, setIsGeneratingNodes] = useState(false);

  // Screenshot-based editing state
  const [screenshotDialogOpen, setScreenshotDialogOpen] = useState(false);
  const [screenshotImage, setScreenshotImage] = useState<string | null>(null);
  const [screenshotCommand, setScreenshotCommand] = useState("");
  const [isProcessingScreenshot, setIsProcessingScreenshot] = useState(false);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  // Visual Flow Editor state (large modal with chat interface)
  const [visualEditorOpen, setVisualEditorOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    image?: string;
    timestamp: Date;
  }>>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatImage, setChatImage] = useState<string | null>(null);
  const [isProcessingChat, setIsProcessingChat] = useState(false);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatImageInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Flow analysis state for auto-suggest
  const [flowAnalysis, setFlowAnalysis] = useState<{
    hasIssues: boolean;
    issues: Array<{
      id: string;
      type: string;
      severity: 'error' | 'warning' | 'info';
      title: string;
      description: string;
      affectedNodes: Array<{ id: string; label: string }>;
      suggestedFix: string;
    }>;
    summary: string;
  } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAnalysisPanel, setShowAnalysisPanel] = useState(false);

  // Autopilot suggestions state
  const [autopilotSuggestions, setAutopilotSuggestions] = useState<Array<{
    id: string;
    text: string;
    category: string;
    priority: "high" | "medium" | "low";
    prompt: string;
  }>>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Persist conversation history to localStorage (per flow)
  const chatStorageKey = flowId ? `flow-chat-history-${flowId}` : null;

  // Load conversation history from localStorage on mount or flowId change
  useEffect(() => {
    if (chatStorageKey) {
      try {
        const stored = localStorage.getItem(chatStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Convert timestamp strings back to Date objects
          const messages = parsed.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          setChatMessages(messages);
        }
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    }
  }, [chatStorageKey]);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    if (chatStorageKey) {
      try {
        if (chatMessages.length > 0) {
          // Keep only the last 50 messages to avoid localStorage limits
          const messagesToStore = chatMessages.slice(-50);
          localStorage.setItem(chatStorageKey, JSON.stringify(messagesToStore));
        }
        // Note: Don't remove from localStorage here when empty - that's handled by Clear button
      } catch (e) {
        console.error('Failed to save chat history:', e);
      }
    }
  }, [chatStorageKey, chatMessages]);

  // Preview state for visual editor (changes are only applied when Save is clicked)
  const [previewNodes, setPreviewNodes] = useState<FlowNode[]>([]);
  const [previewEdges, setPreviewEdges] = useState<FlowEdge[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Handlers for preview nodes/edges changes in Visual Flow Editor
  const onPreviewNodesChange = useCallback(
    (changes: NodeChange<FlowNode>[]) => {
      setPreviewNodes((nds) => applyNodeChanges(changes, nds));
      setHasUnsavedChanges(true);
    },
    []
  );

  const onPreviewEdgesChange = useCallback(
    (changes: EdgeChange<FlowEdge>[]) => {
      setPreviewEdges((eds) => applyEdgeChanges(changes, eds));
      setHasUnsavedChanges(true);
    },
    []
  );

  const onPreviewConnect = useCallback(
    (connection: Connection) => {
      const edgeWithId = {
        ...connection,
        id: `edge-${connection.source}-${connection.target}${connection.sourceHandle ? `-${connection.sourceHandle}` : ""}`,
      };
      setPreviewEdges((eds) => addEdgeWithDeduplication(edgeWithId as FlowEdge, eds));
      setHasUnsavedChanges(true);
    },
    []
  );

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track previous visualEditorOpen state to detect when dialog opens
  const prevVisualEditorOpen = useRef(false);
  const previewNodesSyncedRef = useRef(false);

  // Sync preview state when Visual Editor dialog opens
  // Note: Preview initialization is handled in onOpenChange, this useEffect handles
  // the edge case where nodes load AFTER the dialog is already open
  useEffect(() => {
    const dialogJustOpened = visualEditorOpen && !prevVisualEditorOpen.current;
    const validNodes = Array.isArray(nodes) ? nodes : [];
    const validEdges = Array.isArray(edges) ? edges : [];

    // Helper to create deep copy of nodes with proper structure
    const deepCopyNodes = () => validNodes.map(n => ({
      ...n,
      data: n.data ? { ...n.data, config: n.data.config ? { ...n.data.config } : {} } : { type: 'message', label: 'Node', config: {} }
    }));
    const deepCopyEdges = () => validEdges.map(e => ({ ...e }));

    // Reset sync flag when dialog closes
    if (!visualEditorOpen) {
      previewNodesSyncedRef.current = false;
    }

    // Only sync on dialog open if we haven't synced yet and main canvas has nodes
    // This handles the case where dialog opens before flow data loads
    if (dialogJustOpened && validNodes.length > 0 && !previewNodesSyncedRef.current) {
      console.log('[Visual Editor] Dialog opened via useEffect, syncing preview nodes:', validNodes.length);
      setPreviewNodes(deepCopyNodes());
      setPreviewEdges(deepCopyEdges());
      setHasUnsavedChanges(false);
      previewNodesSyncedRef.current = true;
    }

    // Handle case where nodes load while dialog is already open and we haven't synced
    if (visualEditorOpen && !dialogJustOpened && !previewNodesSyncedRef.current && validNodes.length > 0) {
      console.log('[Visual Editor] Nodes loaded while dialog open, syncing:', validNodes.length);
      setPreviewNodes(deepCopyNodes());
      setPreviewEdges(deepCopyEdges());
      setHasUnsavedChanges(false);
      previewNodesSyncedRef.current = true;
    }

    prevVisualEditorOpen.current = visualEditorOpen;
  }, [visualEditorOpen, nodes, edges]);

  // Fetch autopilot suggestions when AI Flow Editor opens
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!visualEditorOpen || nodes.length === 0) {
        return;
      }

      // Only fetch if we don't already have suggestions
      if (autopilotSuggestions.length > 0) {
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        const response = await apiRequest("POST", "/api/flow-automation/flows/suggestions", {
          nodes,
          edges,
        });
        const data = await response.json();

        if (data.success && data.suggestions) {
          setAutopilotSuggestions(data.suggestions);
        }
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
      } finally {
        setIsLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [visualEditorOpen, nodes, edges, autopilotSuggestions.length]);

  // Clear suggestions when flow changes significantly
  useEffect(() => {
    setAutopilotSuggestions([]);
  }, [flowId]);

  // Undo/Redo history state
  const [history, setHistory] = useState<{ nodes: FlowNode[]; edges: FlowEdge[] }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Save current state to history (called when nodes/edges change)
  const saveToHistory = useCallback((newNodes: FlowNode[], newEdges: FlowEdge[]) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      newHistory.push({ nodes: newNodes, edges: newEdges });
      // Limit history to 50 entries
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo function
  const undo = useCallback(() => {
    if (historyIndex <= 0) return;

    isUndoRedoAction.current = true;
    const newIndex = historyIndex - 1;
    const prevState = history[newIndex];

    if (prevState) {
      setNodes(prevState.nodes);
      setEdges(prevState.edges);
      setHistoryIndex(newIndex);
      setSelectedNode(null);
      toast({
        title: "Undo",
        description: "Change undone",
      });
    }
  }, [history, historyIndex, setNodes, setEdges, toast]);

  // Redo function
  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;

    isUndoRedoAction.current = true;
    const newIndex = historyIndex + 1;
    const nextState = history[newIndex];

    if (nextState) {
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(newIndex);
      setSelectedNode(null);
      toast({
        title: "Redo",
        description: "Change redone",
      });
    }
  }, [history, historyIndex, setNodes, setEdges, toast]);

  // Track changes to nodes/edges for history
  useEffect(() => {
    if (nodes.length > 0 || edges.length > 0) {
      const timeoutId = setTimeout(() => {
        saveToHistory(nodes, edges);
      }, 500); // Debounce to avoid saving every small change
      return () => clearTimeout(timeoutId);
    }
  }, [nodes, edges, saveToHistory]);

  // Helper to get edge label based on source node type and handle
  const getEdgeLabel = useCallback((sourceNodeId: string, sourceHandle: string | null | undefined): string | undefined => {
    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    if (!sourceNode) return undefined;

    const nodeType = sourceNode.data?.type;
    const config = sourceNode.data?.config;

    // Condition nodes: true/false
    if (nodeType === 'condition') {
      if (sourceHandle === 'true') return 'Yes';
      if (sourceHandle === 'false') return 'No';
    }

    // MultiCondition/IntentRouter nodes: use the intent name
    if (nodeType === 'multiCondition' || nodeType === 'intentRouter') {
      if (sourceHandle && config?.intents) {
        const intent = config.intents.find((i: any) => i.name === sourceHandle);
        return intent?.name?.replace(/_/g, ' ') || sourceHandle;
      }
    }

    // Question nodes with branching: use the response option label
    if (nodeType === 'question' && config?.enableBranching && config?.responseOptions) {
      const option = config.responseOptions.find((opt: ResponseOption) => opt.id === sourceHandle);
      return option?.label || sourceHandle;
    }

    // Gatekeeper nodes: fixed handle labels
    if (nodeType === 'gatekeeperDetection' || nodeType === 'gatekeeperBypass') {
      const gatekeeperLabels: Record<string, string> = {
        'dm_answered': 'DM Answered',
        'gatekeeper_answered': 'Gatekeeper',
        'voicemail_detected': 'Voicemail',
        'wrong_number': 'Wrong #',
      };
      if (sourceHandle && gatekeeperLabels[sourceHandle]) {
        return gatekeeperLabels[sourceHandle];
      }
    }

    return undefined;
  }, [nodes]);

  // Click-based connection handler
  const onHandleClick = useCallback((nodeId: string, handleType: 'source' | 'target', handleId?: string) => {
    if (!pendingConnection) {
      // First click - store the source
      if (handleType === 'source') {
        setPendingConnection({ nodeId, handleType, handleId });
        toast({
          title: "Connection started",
          description: "Now click on a target handle (top circle) of another node",
        });
      } else {
        // Clicked on target first - store it and wait for source
        setPendingConnection({ nodeId, handleType });
        toast({
          title: "Target selected",
          description: "Now click on a source handle (bottom circle) of another node",
        });
      }
    } else {
      // Second click - complete the connection
      if (pendingConnection.handleType === 'source' && handleType === 'target') {
        // Valid: source -> target
        if (pendingConnection.nodeId === nodeId) {
          toast({
            title: "Invalid connection",
            description: "Cannot connect a node to itself",
            variant: "destructive",
          });
          setPendingConnection(null);
          return;
        }

        // Get edge label if connecting from a branching node
        const edgeLabel = getEdgeLabel(pendingConnection.nodeId, pendingConnection.handleId);

        const newEdge = {
          id: `edge-${pendingConnection.nodeId}-${nodeId}${pendingConnection.handleId ? `-${pendingConnection.handleId}` : ''}`,
          source: pendingConnection.nodeId,
          target: nodeId,
          sourceHandle: pendingConnection.handleId || null,
          animated: true,
          ...(edgeLabel ? { type: 'labeled', data: { label: edgeLabel, sourceHandle: pendingConnection.handleId } } : {}),
        };

        // Use deduplication to prevent duplicate edges
        setEdges((eds) => addEdgeWithDeduplication(newEdge as FlowEdge, eds));
        toast({
          title: "Connected!",
          description: "Nodes have been connected successfully",
        });
        setPendingConnection(null);
      } else if (pendingConnection.handleType === 'target' && handleType === 'source') {
        // Valid: clicked target first, now clicking source
        if (pendingConnection.nodeId === nodeId) {
          toast({
            title: "Invalid connection",
            description: "Cannot connect a node to itself",
            variant: "destructive",
          });
          setPendingConnection(null);
          return;
        }

        // Get edge label if connecting from a branching node
        const edgeLabel = getEdgeLabel(nodeId, handleId);

        const newEdge = {
          id: `edge-${nodeId}-${pendingConnection.nodeId}${handleId ? `-${handleId}` : ''}`,
          source: nodeId,
          target: pendingConnection.nodeId,
          sourceHandle: handleId || null,
          animated: true,
          ...(edgeLabel ? { type: 'labeled', data: { label: edgeLabel, sourceHandle: handleId } } : {}),
        };

        // Use deduplication to prevent duplicate edges
        setEdges((eds) => addEdgeWithDeduplication(newEdge as FlowEdge, eds));
        toast({
          title: "Connected!",
          description: "Nodes have been connected successfully",
        });
        setPendingConnection(null);
      } else {
        // Same type clicked - restart with new selection
        setPendingConnection({ nodeId, handleType, handleId });
        toast({
          title: handleType === 'source' ? "Source changed" : "Target changed",
          description: `Now click on a ${handleType === 'source' ? 'target (top)' : 'source (bottom)'} handle`,
        });
      }
    }
  }, [pendingConnection, setEdges, toast, getEdgeLabel]);

  // Cancel pending connection on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingConnection) {
        setPendingConnection(null);
        toast({
          title: "Connection cancelled",
          description: "Press Escape or click elsewhere to cancel",
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pendingConnection, toast]);

  // AI content generation mutation
  const generateContentMutation = useMutation({
    mutationFn: async ({
      nodeType,
      fieldType,
      context,
    }: {
      nodeType: string;
      fieldType: string;
      context?: string;
    }) => {
      const res = await apiRequest(
        "POST",
        "/api/flow-automation/flows/generate-content",
        { nodeType, fieldType, context }
      );
      return res.json();
    },
    onSuccess: (data: { content: string }, variables) => {
      // Update the node config with generated content
      updateNodeConfig({ [variables.fieldType]: data.content });
      toast({
        title: t("flows.toast.contentGenerated"),
        description: t("flows.toast.contentGeneratedDescription"),
      });
    },
    onError: (error: any) => {
      toast({
        title: t("flows.toast.generationError"),
        description: error.message || "Failed to generate content",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setGeneratingField(null);
    },
  });

  // State for insertion suggestion
  const [insertionSuggestion, setInsertionSuggestion] = useState<{
    insertAfterNodeId: string;
    insertAfterNodeLabel: string;
    reason: string;
    preview: string;
  } | null>(null);
  const [showInsertionDialog, setShowInsertionDialog] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState("");

  // AI flow generation from prompt
  const generateFlowFromPrompt = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Enter a prompt",
        description: "Describe what you want your flow to do",
        variant: "destructive",
      });
      return;
    }

    // If there are existing nodes, first get suggestion for where to insert
    if (nodes.length > 0) {
      setIsGeneratingNodes(true);
      try {
        const suggestRes = await apiRequest("POST", "/api/flow-automation/flows/suggest-insertion", {
          prompt: aiPrompt.trim(),
          existingNodes: nodes.map(n => ({
            id: n.id,
            type: n.data?.type || "unknown",
            label: n.data?.label || "Unknown"
          })),
          language: "en",
        });
        const suggestData = await suggestRes.json();

        if (suggestData.success && suggestData.suggestion) {
          setInsertionSuggestion(suggestData.suggestion);
          setPendingPrompt(aiPrompt.trim());
          setShowInsertionDialog(true);
          setIsGeneratingNodes(false);
          return;
        }
      } catch (error) {
        console.error("Suggestion failed, proceeding with append mode:", error);
      }
      setIsGeneratingNodes(false);
    }

    // If no existing nodes or suggestion failed, generate normally
    await executeGeneration(aiPrompt.trim(), null);
  };

  // Execute the actual generation (after user confirms insertion point)
  const executeGeneration = async (prompt: string, insertAfterNodeId: string | null) => {
    setIsGeneratingNodes(true);
    setShowInsertionDialog(false);

    try {
      // Find the node to insert after
      const insertAfterNode = insertAfterNodeId
        ? nodes.find(n => n.id === insertAfterNodeId)
        : nodes[nodes.length - 1];

      const res = await apiRequest("POST", "/api/flow-automation/flows/generate", {
        prompt: prompt,
        language: "en",
        saveImmediately: false,
        appendMode: nodes.length > 0,
        existingNodes: nodes.map(n => ({
          id: n.id,
          type: n.data?.type || "unknown",
          label: n.data?.label || "Unknown",
          data: n.data
        })),
      });
      const data = await res.json();

      if (data.flow?.nodes && data.flow?.edges) {
        // Calculate position for new nodes (below the insertion point)
        const insertY = insertAfterNode
          ? (insertAfterNode.position?.y || 0) + 120
          : 100;
        const insertX = insertAfterNode
          ? insertAfterNode.position?.x || 250
          : 250;

        // Transform generated nodes to match our format and position them
        const newNodes: FlowNode[] = data.flow.nodes.map((node: any, index: number) => ({
          id: `ai-${Date.now()}-${index}`,
          type: "custom",
          position: {
            x: insertX + (node.position?.x || 0) - 250, // Offset from center
            y: insertY + index * 120,
          },
          data: {
            type: node.data?.type || node.type || "message",
            label: node.data?.label || node.data?.config?.label || `Node ${index + 1}`,
            config: node.data?.config || node.data || {},
          },
        }));

        // Create a mapping from old IDs to new IDs
        const idMap = new Map<string, string>();
        data.flow.nodes.forEach((node: any, index: number) => {
          idMap.set(node.id, newNodes[index].id);
        });

        // Transform edges with new IDs
        const newEdges: FlowEdge[] = data.flow.edges.map((edge: any, index: number) => ({
          id: `ai-edge-${Date.now()}-${index}`,
          source: idMap.get(edge.source) || edge.source,
          target: idMap.get(edge.target) || edge.target,
          sourceHandle: edge.sourceHandle || null,
          animated: true,
        }));

        // Add connection from insertion point to first new node
        if (insertAfterNode && newNodes.length > 0) {
          const connectingEdge: FlowEdge = {
            id: `ai-connect-${Date.now()}`,
            source: insertAfterNode.id,
            target: newNodes[0].id,
            sourceHandle: null,
            animated: true,
          };
          newEdges.unshift(connectingEdge);
        }

        // Add to existing nodes/edges (merge mode)
        setNodes((prev) => [...prev, ...newNodes]);
        setEdges((prev) => [...prev, ...newEdges]);

        toast({
          title: "Nodes added!",
          description: `Added ${newNodes.length} nodes after "${insertAfterNode?.data?.label || 'last node'}"`,
        });

        setAiPrompt("");
        setPendingPrompt("");
        setInsertionSuggestion(null);
      } else {
        throw new Error(data.error || "Failed to generate flow");
      }
    } catch (error: any) {
      toast({
        title: "Generation failed",
        description: error.message || "Could not generate flow from prompt",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingNodes(false);
    }
  };

  // Handle user confirmation of insertion point
  const handleInsertionConfirm = () => {
    if (insertionSuggestion && pendingPrompt) {
      executeGeneration(pendingPrompt, insertionSuggestion.insertAfterNodeId);
    }
  };

  // Handle user selecting a different insertion point
  const handleInsertionChange = (nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node && insertionSuggestion) {
      setInsertionSuggestion({
        ...insertionSuggestion,
        insertAfterNodeId: nodeId,
        insertAfterNodeLabel: node.data?.label || "Selected Node"
      });
    }
  };

  // Available node types for the sidebar (using translations)
  const availableNodeTypes = [
    // ⭐ STATE MACHINE - Recommended for complex flows
    {
      type: "stateMachine",
      label: t("flows.nodeTypes.stateMachine", "⭐ State Machine"),
      description: t("flows.nodeDescriptions.stateMachine", "Consolidate 10+ nodes into 1"),
      icon: GitBranch,
      help: "⭐ RECOMMENDED: Consolidate multiple conversation steps into a single node. Define internal states, transitions, and exit points. Reduces complex 40-50 node flows to 8-12 nodes while maintaining all functionality.",
    },
    {
      type: "message",
      label: t("flows.nodeTypes.message"),
      description: t("flows.nodeDescriptions.message"),
      icon: MessageSquare,
      help: "Use this to make the AI speak a message to the caller. Enable 'Wait for response' if you want to pause for the caller to reply.",
    },
    {
      type: "recordingConsent",
      label: t("flows.nodeTypes.recordingConsent", "Recording Disclosure"),
      description: t("flows.nodeDescriptions.recordingConsent", "Compliance: Inform caller that call is recorded"),
      icon: ShieldAlert,
      help: "GDPR/Compliance requirement: Inform the caller that the call may be recorded for training and quality monitoring. This should be placed immediately after the greeting.",
    },
    {
      type: "question",
      label: t("flows.nodeTypes.question"),
      description: t("flows.nodeDescriptions.question"),
      icon: HelpCircle,
      help: "Ask a specific question and save the answer to a variable. Use the variable name (e.g., 'customer_name') in conditions or other nodes.",
    },
    {
      type: "condition",
      label: t("flows.nodeTypes.condition"),
      description: t("flows.nodeDescriptions.condition"),
      icon: GitBranch,
      help: "Create branching logic based on caller responses. Has two outputs: Green (Yes/True) and Red (No/False). Example: 'caller wants to speak to sales'",
    },
    {
      type: "appointment",
      label: t("flows.nodeTypes.appointment"),
      description: t("flows.nodeDescriptions.appointment"),
      icon: Calendar,
      help: "Book an appointment with the caller. Specify the appointment type (e.g., 'consultation', 'demo') to help the AI understand the context.",
    },
    {
      type: "form",
      label: t("flows.nodeTypes.form"),
      description: t("flows.nodeDescriptions.form"),
      icon: FileText,
      help: "Collect multiple pieces of information using a pre-built form. Create forms in the Forms section first, then select them here.",
    },
    {
      type: "webhook",
      label: t("flows.nodeTypes.webhook"),
      description: t("flows.nodeDescriptions.webhook"),
      icon: Webhook,
      help: "Send collected data to an external API/system. Useful for CRM integration, notifications, or custom processing.",
    },
    {
      type: "transfer",
      label: t("flows.nodeTypes.transfer"),
      description: t("flows.nodeDescriptions.transfer"),
      icon: Phone,
      help: "Transfer the call to a human agent or another phone number. The AI will hand off the conversation.",
    },
    {
      type: "delay",
      label: t("flows.nodeTypes.delay"),
      description: t("flows.nodeDescriptions.delay"),
      icon: Clock,
      help: "Add a pause in the conversation. Useful for waiting for processing or creating natural conversation rhythm.",
    },
    {
      type: "callback",
      label: t("flows.nodeTypes.callback", "Schedule Callback"),
      description: t("flows.nodeDescriptions.callback", "Schedule a callback for later"),
      icon: PhoneForwarded,
      help: "Schedule a callback when the customer requests to be called later. The AI will collect the preferred time and automatically schedule the callback.",
    },
    {
      type: "end",
      label: t("flows.nodeTypes.end"),
      description: t("flows.nodeDescriptions.end"),
      icon: StopCircle,
      help: "End the conversation with a goodbye message. Every flow should have at least one End node.",
    },
    // State Machine Nodes (Advanced)
    {
      type: "subFlowInvoke",
      label: t("flows.nodeTypes.subFlowInvoke", "Sub-Flow"),
      description: t("flows.nodeDescriptions.subFlowInvoke", "Invoke a reusable sub-flow"),
      icon: GitBranch,
      help: "Invoke a reusable sub-flow (e.g., objection handling). Sub-flows can be defined once and used in multiple places, with different exit paths mapped to different nodes.",
    },
    {
      type: "stateTransition",
      label: t("flows.nodeTypes.stateTransition", "State Transition"),
      description: t("flows.nodeDescriptions.stateTransition", "Trigger campaign state change"),
      icon: GitBranch,
      help: "Mark a transition point in the campaign state machine. Used for multi-call workflows where contacts progress through states like DIAL → GATEKEEPER → DECISION_MAKER → BOOKING.",
    },
    {
      type: "persistVariable",
      label: t("flows.nodeTypes.persistVariable", "Persist Data"),
      description: t("flows.nodeDescriptions.persistVariable", "Save data for future calls"),
      icon: FileText,
      help: "Mark variables to be persisted across calls. Captured data (like dm_name, pain_points) will be available in subsequent calls to this contact.",
    },
  ];

  // Fetch agents for selection
  const { data: agents } = useQuery<any[]>({
    queryKey: ["/api/agents"],
  });

  // Fetch forms for form node selection
  const { data: availableForms } = useQuery<
    Array<{
      id: string;
      name: string;
      description: string | null;
      fields?: Array<{
        id: string;
        question: string;
        fieldType: string;
        isRequired: boolean;
        order: number;
      }>;
    }>
  >({
    queryKey: ["/api/flow-automation/forms"],
  });

  // Fetch selected form details when a form node is selected
  const selectedFormId = selectedNode?.data.config?.formId;
  const {
    data: selectedFormDetails,
    isLoading: isLoadingFormDetails,
    error: formDetailsError,
  } = useQuery<{
    id: string;
    name: string;
    description: string | null;
    fields?: Array<{
      id: string;
      question: string;
      fieldType: string;
      isRequired: boolean;
      order: number;
    }>;
  }>({
    queryKey: [`/api/flow-automation/forms/${selectedFormId}`],
    enabled: !!selectedFormId && selectedNode?.data.type === "form",
  });

  // Fetch connected calendars for appointment node calendar selection
  const { data: connectedCalendars } = useQuery<{
    calendars: Array<{
      email: string;
      provider: 'google' | 'microsoft';
      isPrimary: boolean;
    }>;
    defaultProvider: 'google' | 'microsoft';
  }>({
    queryKey: ["/api/flow-automation/connected-calendars"],
  });

  // Load existing flow if editing
  const { data: flow } = useQuery<any>({
    queryKey: [`/api/flow-automation/flows/${flowId}`],
    enabled: !!flowId,
  });

  // Outbound compatibility validation - runs when flow is saved or nodes change significantly
  const { data: outboundValidation, refetch: refetchValidation } = useQuery<{
    isCompatible: boolean;
    compatibilityLevel: 'fully_compatible' | 'requires_agent_config' | 'incompatible' | 'unknown';
    issues: Array<{
      code: string;
      severity: 'error' | 'warning' | 'info';
      nodeId?: string;
      nodeLabel?: string;
      message: string;
      resolution?: string;
    }>;
    requirements: {
      needsTransferNumber: boolean;
      needsHostEmail: boolean;
      needsCalendarConnection: boolean;
      unresolvedVariables: string[];
    };
  }>({
    queryKey: [`/api/flow-automation/flows/${flowId}/validate-outbound`],
    enabled: !!flowId && nodes.length > 0,
    staleTime: 30000, // Cache for 30 seconds to avoid excessive API calls
    refetchOnWindowFocus: false,
  });

  // Load flow data when fetched
  useEffect(() => {
    if (flow) {
      console.log('[FlowBuilder] Loading flow from API:', flow.id);
      console.log('[FlowBuilder] Raw nodes from API:', JSON.stringify(flow.nodes, null, 2));

      // Valid node types that are registered in nodeTypes
      const validNodeTypes = ['message', 'recordingConsent', 'question', 'condition', 'appointment', 'form', 'webhook', 'transfer', 'delay', 'end', 'custom', 'flowNode', 'callback'];

      // Transform nodes to ensure they have proper structure for ReactFlow
      const transformedNodes = ((flow.nodes as FlowNode[]) || []).map(node => {
        // Determine the actual node type from data.type or node.type
        const actualType = node.data?.type || node.type || "message";

        // Use the actual type if it's valid, otherwise fallback to "custom"
        const reactFlowType = validNodeTypes.includes(actualType) ? actualType :
                             validNodeTypes.includes(node.type || '') ? node.type : 'custom';

        const transformed: FlowNode = {
          ...node,
          // Use a valid ReactFlow type
          type: reactFlowType as string,
          // Ensure position is defined
          position: node.position || { x: 250, y: 100 },
          // Ensure nodes are interactive - these might be false/undefined in saved data
          draggable: true,
          selectable: true,
          // Ensure data structure is complete - spread node.data first, then override
          data: {
            ...node.data,
            type: actualType,
            label: node.data?.label || actualType.charAt(0).toUpperCase() + actualType.slice(1),
            config: node.data?.config || {},
          }
        };
        console.log('[FlowBuilder] Transformed node:', node.id, '| type:', transformed.type, '| data.type:', transformed.data.type, '| position:', transformed.position);
        return transformed;
      });

      console.log('[FlowBuilder] All transformed nodes count:', transformedNodes.length);
      setNodes(transformedNodes);
      // Deduplicate edges when loading to fix any existing duplicate edge issues
      const loadedEdges = (flow.edges as FlowEdge[]) || [];
      const deduplicatedEdges = deduplicateFlowEdges(loadedEdges);
      setEdges(deduplicatedEdges);
      setFlowName(flow.name);
      setFlowDescription(flow.description || "");
      setAgentId(flow.agentId || "");
      setFirstMessage(flow.firstMessage || "");
    }
  }, [flow, setNodes, setEdges]);

  // Handle connection creation with validation
  const onConnect = useCallback(
    (connection: Connection) => {
      // Prevent self-connections
      if (connection.source === connection.target) {
        toast({
          title: t("flows.toast.invalidConnection"),
          description: t("flows.toast.cannotConnectSelf"),
          variant: "destructive",
        });
        return;
      }

      // Get edge label if connecting from a branching node
      const edgeLabel = connection.source ? getEdgeLabel(connection.source, connection.sourceHandle) : undefined;

      // Add edge with ID immediately (required for state management)
      const edgeWithId = {
        ...connection,
        id: `edge-${connection.source}-${connection.target}${connection.sourceHandle ? `-${connection.sourceHandle}` : ""}`,
        animated: true,
        ...(edgeLabel ? { type: 'labeled', data: { label: edgeLabel, sourceHandle: connection.sourceHandle } } : {}),
      };

      // Use deduplication to prevent duplicate edges (which cause ElevenLabs repetition issues)
      setEdges((eds) => addEdgeWithDeduplication(edgeWithId as FlowEdge, eds));
    },
    [setEdges, toast, t, getEdgeLabel]
  );

  // Handle node click (single click to select)
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('[FlowBuilder] onNodeClick fired:', node.id, 'type:', node.type, 'data:', node.data);
    setSelectedNode(node as FlowNode);
  }, []);

  // Alternative selection handler - fires when ReactFlow's internal selection changes
  const onSelectionChange = useCallback(({ nodes: selectedNodes }: { nodes: Node[] }) => {
    console.log('[FlowBuilder] onSelectionChange fired, selected nodes:', selectedNodes.length);
    if (selectedNodes.length > 0) {
      const node = selectedNodes[0];
      console.log('[FlowBuilder] Setting selected node from onSelectionChange:', node.id);
      setSelectedNode(node as FlowNode);
    }
  }, []);

  // Debug: Check if nodes are draggable
  const onNodeDragStart = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('[FlowBuilder] onNodeDragStart fired:', node.id);
  }, []);

  // Flow validation function - checks for common configuration issues
  const validateFlow = useCallback((nodesToValidate: FlowNode[]): { isValid: boolean; errors: string[]; warnings: string[] } => {
    const errors: string[] = [];
    const warnings: string[] = [];

    for (const node of nodesToValidate) {
      const nodeType = node.data?.type || node.type;
      const config = node.data?.config || {};
      const nodeLabel = config.label || config.name || nodeType || 'Node';

      // Validate Transfer nodes
      if (nodeType === 'transfer') {
        const phoneNumber = config.phoneNumber || '';

        // Template variables like {{transfer_number}} are valid - they get substituted from agent settings
        const isTemplateVariable = /\{\{.+\}\}/.test(phoneNumber);

        // Check for missing phone number - but allow empty if it will be filled from agent settings
        if (!phoneNumber || phoneNumber.trim() === '') {
          // Empty is OK - auto-filled with {{transfer_number}} which uses agent's transfer number
          // Just add a warning instead of an error
          warnings.push(`Transfer node "${nodeLabel}" has no phone number set. It will use the agent's configured transfer number.`);
        }
        // Skip validation for template variables - they're filled from agent settings at runtime
        else if (!isTemplateVariable) {
          // Check for placeholder phone numbers
          if (
            phoneNumber === '+1234567890' ||
            phoneNumber === '1234567890' ||
            phoneNumber === '+0000000000' ||
            phoneNumber === '0000000000' ||
            /^(\+?0{10,}|\+?1234567890)$/.test(phoneNumber) ||
            phoneNumber.replace(/\D/g, '').length < 10
          ) {
            errors.push(`Transfer node "${nodeLabel}" has a placeholder or invalid phone number (${phoneNumber}). Please enter a real phone number.`);
          }
        }
        // Template variable is valid - no error needed
      }

      // Validate Appointment nodes
      if (nodeType === 'appointment') {
        // Check if calendar is connected (warning, not error - booking can still work via legacy method)
        if (!connectedCalendars?.calendars || connectedCalendars.calendars.length === 0) {
          warnings.push(`Appointment node "${nodeLabel}": No calendar connected. Connect Google or Microsoft calendar in Settings for automatic booking with calendar invites.`);
        }
      }

      // Validate Webhook nodes
      if (nodeType === 'webhook') {
        const webhookUrl = config.webhookUrl || config.url || '';
        if (!webhookUrl || webhookUrl.trim() === '') {
          warnings.push(`Webhook node "${nodeLabel}" has no URL configured. This webhook will not execute.`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, [connectedCalendars]);

  // Save flow mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      // Validate flow before saving
      const validation = validateFlow(nodes);

      // Show warnings but allow save
      if (validation.warnings.length > 0) {
        validation.warnings.forEach(warning => {
          toast({
            title: "Warning",
            description: warning,
            variant: "default",
          });
        });
      }

      // Block save if there are errors
      if (!validation.isValid) {
        const errorMessage = validation.errors.join('\n\n');
        throw new Error(errorMessage);
      }

      // Ensure all edges have IDs (required by backend schema)
      const edgesWithIds = edges.map((edge) => ({
        ...edge,
        id: edge.id || `edge-${edge.source}-${edge.target}`,
      }));

      const flowData = {
        name: flowName,
        description: flowDescription,
        nodes,
        edges: edgesWithIds,
        agentId: agentId || null,
        firstMessage: firstMessage || null,
        isActive: flow?.isActive ?? true,
      };

      let res;
      if (flowId) {
        res = await apiRequest(
          "PATCH",
          `/api/flow-automation/flows/${flowId}`,
          flowData
        );
      } else {
        res = await apiRequest("POST", "/api/flow-automation/flows", flowData);
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: t("flows.toast.saved"),
        description: t("flows.toast.savedDescription"),
      });

      // Show server-side validation warnings (latency, repetition patterns, etc.)
      if (data._validationWarnings && data._validationWarnings.length > 0) {
        data._validationWarnings.slice(0, 3).forEach((warning: any, index: number) => {
          setTimeout(() => {
            toast({
              title: "Flow Suggestion",
              description: warning.message || warning,
              duration: 8000,
            });
          }, (index + 1) * 800);
        });

        if (data._validationWarnings.length > 3) {
          setTimeout(() => {
            toast({
              title: "Additional Suggestions",
              description: `${data._validationWarnings.length - 3} more suggestion(s). Check server logs for details.`,
              duration: 6000,
            });
          }, 3500);
        }
      }

      queryClient.invalidateQueries({
        queryKey: ["/api/flow-automation/flows"],
      });

      // Invalidate the individual flow query to refresh the editor with latest data
      if (flowId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/flow-automation/flows/${flowId}`],
        });
      }

      // Navigate to edit mode if this was a new flow
      if (!flowId) {
        setLocation(`/app/flows/${data.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: t("flows.toast.saveError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Screenshot-based vision editing
  const handleScreenshotPaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            setScreenshotImage(base64);
            setScreenshotDialogOpen(true);
          };
          reader.readAsDataURL(blob);
          e.preventDefault();
          break;
        }
      }
    }
  }, []);

  const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setScreenshotImage(base64);
        setScreenshotDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const processScreenshotEdit = async () => {
    if (!screenshotImage || !screenshotCommand.trim()) {
      toast({
        title: "Missing input",
        description: "Please provide both a screenshot and a command",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingScreenshot(true);

    try {
      const response = await apiRequest("POST", "/api/flow-automation/flows/vision-edit", {
        image: screenshotImage,
        command: screenshotCommand,
        nodes,
        edges,
      });

      const data = await response.json();

      if (data.success) {
        setNodes(data.nodes);
        setEdges(data.edges);

        toast({
          title: "Edit applied!",
          description: `${data.action}: ${data.reasoning}`,
        });

        // Close dialog and reset state
        setScreenshotDialogOpen(false);
        setScreenshotImage(null);
        setScreenshotCommand("");
      } else {
        toast({
          title: "Could not apply edit",
          description: data.message || data.suggestion || "Please try a more specific command",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Vision edit failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessingScreenshot(false);
    }
  };

  // Listen for paste events for screenshot
  useEffect(() => {
    document.addEventListener('paste', handleScreenshotPaste);
    return () => document.removeEventListener('paste', handleScreenshotPaste);
  }, [handleScreenshotPaste]);

  // Handle chat image upload
  const handleChatImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setChatImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Process chat message (text or with image)
  const processChatMessage = async () => {
    if (!chatInput.trim() && !chatImage) return;

    const userMessage = {
      role: 'user' as const,
      content: chatInput.trim(),
      image: chatImage || undefined,
      timestamp: new Date(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput("");
    const currentImage = chatImage;
    setChatImage(null);
    setIsProcessingChat(true);

    try {
      let response: Response;
      let data: {
        success?: boolean;
        action?: string;
        reasoning?: string;
        message?: string;
        error?: string;
        nodes?: FlowNode[];
        edges?: FlowEdge[];
        removedNodeIds?: string[];
        modifiedNodeIds?: string[];
      };

      if (currentImage) {
        // Use vision-edit API for image-based edits
        response = await apiRequest("POST", "/api/flow-automation/flows/vision-edit", {
          image: currentImage,
          command: userMessage.content || "Analyze this screenshot and suggest what changes to make",
          nodes: previewNodes,
          edges: previewEdges,
        });
        data = await response.json();

        if (data.success && data.nodes && data.edges) {
          // Update preview state (not main state)
          setPreviewNodes(data.nodes);
          setPreviewEdges(data.edges);
          setHasUnsavedChanges(true);

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ Preview updated! ${(data.action || 'edit').replace('_', ' ')}: ${data.reasoning || 'Changes ready'} (Click Save to apply)`,
            timestamp: new Date(),
          }]);
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ ${data.message || "Could not apply the edit. Please try being more specific."}`,
            timestamp: new Date(),
          }]);
        }
      } else {
        // Use text-based edit API
        response = await apiRequest("POST", "/api/flow-automation/flows/edit", {
          prompt: userMessage.content,
          nodes: previewNodes,
          edges: previewEdges,
        });
        data = await response.json();

        if (data.success && data.nodes && data.edges) {
          // Update preview state (not main state)
          setPreviewNodes(data.nodes);
          setPreviewEdges(data.edges);
          setHasUnsavedChanges(true);

          const changes: string[] = [];
          if (data.removedNodeIds?.length) changes.push(`removed ${data.removedNodeIds.length} node(s)`);
          if (data.modifiedNodeIds?.length) changes.push(`modified ${data.modifiedNodeIds.length} node(s)`);
          if (data.nodes.length > previewNodes.length) changes.push(`added ${data.nodes.length - previewNodes.length} node(s)`);

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ Preview updated! ${changes.join(', ') || 'Changes ready.'} (Click Save to apply)`,
            timestamp: new Date(),
          }]);
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ ${data.error || data.message || "Could not apply the edit. Please try again."}`,
            timestamp: new Date(),
          }]);
        }
      }
    } catch (error: any) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessingChat(false);
      // Scroll to bottom
      setTimeout(() => {
        chatContainerRef.current?.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  // Analyze flow for issues - auto-suggest feature
  const analyzeFlow = async () => {
    if (nodes.length === 0) {
      toast({
        title: "No Flow to Analyze",
        description: "Add some nodes to the flow first",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setShowAnalysisPanel(true);

    try {
      const response = await apiRequest("POST", "/api/flow-automation/flows/analyze", {
        nodes,
        edges,
      });
      const data = await response.json();

      if (data.success) {
        setFlowAnalysis(data);

        // Add analysis results to chat
        if (data.hasIssues) {
          const issuesList = data.issues.map((issue: any) =>
            `• **${issue.title}** (${issue.severity}): ${issue.description}`
          ).join('\n');

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `🔍 **Flow Analysis Complete**\n\n${data.summary}\n\n${issuesList}\n\n💡 Click "Fix All Issues" to automatically fix these, or ask me to fix specific issues.`,
            timestamp: new Date(),
          }]);
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ **Flow Analysis Complete**\n\n${data.summary}`,
            timestamp: new Date(),
          }]);
        }
      }
    } catch (error: any) {
      toast({
        title: "Analysis Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Fix all detected issues
  const fixAllIssues = async () => {
    if (!flowAnalysis?.hasIssues) return;

    setIsProcessingChat(true);

    // Build a comprehensive fix prompt
    const issueDescriptions = flowAnalysis.issues.map(issue => {
      if (issue.type === 'orphan') {
        return `Connect orphan nodes: ${issue.affectedNodes.map(n => n.label).join(', ')}`;
      } else if (issue.type === 'dead_end') {
        return `Fix dead-end nodes: ${issue.affectedNodes.map(n => n.label).join(', ')}`;
      } else if (issue.type === 'missing_branch') {
        return `Add missing condition branches`;
      } else if (issue.type === 'missing_intent_edge') {
        return `Add missing multiCondition intent edges`;
      } else if (issue.type === 'missing_gatekeeper_branch') {
        return `Connect all 4 gatekeeper branches: ${issue.affectedNodes.map(n => n.label).join(', ')}. Each gatekeeperDetection node needs dm_answered→pitch, gatekeeper_answered→transfer, voicemail_detected→voicemail message, wrong_number→polite exit`;
      } else if (issue.type === 'missing_end') {
        return `Add an end node`;
      }
      return issue.suggestedFix;
    }).join('; ');

    const fixPrompt = `Fix all the following issues: ${issueDescriptions}`;

    setChatMessages(prev => [...prev, {
      role: 'user',
      content: `🔧 Fix All Issues`,
      timestamp: new Date(),
    }]);

    try {
      const response = await apiRequest("POST", "/api/flow-automation/flows/edit", {
        prompt: fixPrompt,
        nodes,
        edges,
      });
      const data = await response.json();

      if (data.success && data.nodes && data.edges) {
        setNodes(data.nodes);
        setEdges(data.edges);
        setFlowAnalysis(null);
        setShowAnalysisPanel(false);

        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ **All Issues Fixed!**\n\n${data.summary || 'Flow has been repaired.'}`,
          timestamp: new Date(),
        }]);

        toast({
          title: "Issues Fixed",
          description: "All detected issues have been resolved",
        });
      } else {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `❌ ${data.error || 'Could not fix all issues. Try fixing them individually.'}`,
          timestamp: new Date(),
        }]);
      }
    } catch (error: any) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessingChat(false);
    }
  };

  // Process inline chat message - works directly on main canvas nodes
  const processInlineChatMessage = async () => {
    if (!chatInput.trim() && !chatImage) return;

    const userMessage = {
      role: 'user' as const,
      content: chatInput.trim(),
      image: chatImage || undefined,
      timestamp: new Date(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput("");
    const currentImage = chatImage;
    setChatImage(null);
    setIsProcessingChat(true);

    try {
      let response: Response;
      let data: {
        success?: boolean;
        action?: string;
        reasoning?: string;
        message?: string;
        error?: string;
        nodes?: FlowNode[];
        edges?: FlowEdge[];
        removedNodeIds?: string[];
        modifiedNodeIds?: string[];
      };

      if (currentImage) {
        // Build conversation history for context
        const conversationHistory = chatMessages
          .filter(msg => msg.content && !msg.content.startsWith('✅') && !msg.content.startsWith('❌'))
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }));

        // Use vision-edit API for image-based edits
        response = await apiRequest("POST", "/api/flow-automation/flows/vision-edit", {
          image: currentImage,
          command: userMessage.content || "Analyze this screenshot and suggest what changes to make",
          nodes: nodes,
          edges: edges,
          conversationHistory: conversationHistory,
        });
        data = await response.json();

        if (data.success && data.nodes && data.edges) {
          // Update main canvas directly
          setNodes(data.nodes);
          setEdges(data.edges);

          // Build detailed change summary
          const changeDetails: string[] = [];
          const action = (data.action || 'edit').replace('_', ' ');

          // Show removed nodes with their names
          if (data.removedNodes?.length) {
            const removedNames = data.removedNodes.map((n: { label: string }) =>
              `"${n.label}"`
            ).join(', ');
            changeDetails.push(`Removed: ${removedNames}`);
          }

          // Show modified nodes with their names
          if (data.modifiedNodesInfo?.length) {
            const modifiedNames = data.modifiedNodesInfo.map((n: { label: string }) =>
              `"${n.label}"`
            ).join(', ');
            changeDetails.push(`Modified: ${modifiedNames}`);
          }

          // Use reasoning or detailed changes
          const summaryText = changeDetails.length > 0
            ? `${action}: ${changeDetails.join(' | ')}`
            : `${action}: ${data.reasoning || 'Changes applied'}`;

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ ${summaryText}`,
            timestamp: new Date(),
          }]);

          toast({
            title: "Flow Updated",
            description: summaryText.length > 100 ? summaryText.substring(0, 100) + '...' : summaryText,
          });
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ ${data.message || "Could not apply the edit. Please try being more specific."}`,
            timestamp: new Date(),
          }]);
        }
      } else {
        // Build conversation history from chat messages for context
        const conversationHistory = chatMessages
          .filter(msg => msg.content && !msg.content.startsWith('✅') && !msg.content.startsWith('❌'))
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }));

        // Use text-based edit API with conversation history
        response = await apiRequest("POST", "/api/flow-automation/flows/edit", {
          prompt: userMessage.content,
          nodes: nodes,
          edges: edges,
          conversationHistory: conversationHistory,
        });
        data = await response.json();

        if (data.success && data.nodes && data.edges) {
          // Update main canvas directly
          setNodes(data.nodes);
          setEdges(data.edges);

          // Build detailed change summary
          const changeDetails: string[] = [];

          // Show added nodes with their names and types
          if (data.addedNodes?.length) {
            const addedNames = data.addedNodes.map((n: { label: string; type: string }) =>
              `"${n.label}" (${n.type})`
            ).join(', ');
            changeDetails.push(`Added: ${addedNames}`);
          }

          // Show removed nodes with their names
          if (data.removedNodes?.length) {
            const removedNames = data.removedNodes.map((n: { label: string }) =>
              `"${n.label}"`
            ).join(', ');
            changeDetails.push(`Removed: ${removedNames}`);
          }

          // Show modified nodes with their names
          if (data.modifiedNodesInfo?.length) {
            const modifiedNames = data.modifiedNodesInfo.map((n: { label: string }) =>
              `"${n.label}"`
            ).join(', ');
            changeDetails.push(`Modified: ${modifiedNames}`);
          }

          // Use AI summary if available, otherwise use detailed changes
          const summaryText = data.summary || changeDetails.join(' | ') || 'Changes applied.';

          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `✅ ${summaryText}`,
            timestamp: new Date(),
          }]);

          toast({
            title: "Flow Updated",
            description: summaryText.length > 100 ? summaryText.substring(0, 100) + '...' : summaryText,
          });
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `❌ ${data.error || data.message || "Could not apply the edit. Please try again."}`,
            timestamp: new Date(),
          }]);
        }
      }
    } catch (error: any) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsProcessingChat(false);
      // Scroll to bottom
      setTimeout(() => {
        chatContainerRef.current?.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  // Add new node to canvas
  const addNode = (type: string) => {
    const nodeLabel =
      availableNodeTypes.find((n) => n.type === type)?.label || type;
    const newNode: FlowNode = {
      id: `node-${Date.now()}`,
      type: "custom",
      position: { x: 250, y: 50 + nodes.length * 100 },
      data: {
        type,
        label: nodeLabel,
        config: {
          type,
        },
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
  };

  // Update selected node configuration
  const updateNodeConfig = (config: any) => {
    if (!selectedNode) return;

    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? {
              ...node,
              data: {
                ...node.data,
                config: { ...node.data.config, ...config },
              },
            }
          : node
      )
    );

    // Update selected node state
    setSelectedNode((prev) =>
      prev
        ? {
            ...prev,
            data: { ...prev.data, config: { ...prev.data.config, ...config } },
          }
        : null
    );
  };

  // Delete selected node
  const deleteNode = useCallback(() => {
    if (!selectedNode) return;

    setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
    setEdges((eds) =>
      eds.filter(
        (edge) =>
          edge.source !== selectedNode.id && edge.target !== selectedNode.id
      )
    );
    setSelectedNode(null);
    toast({
      title: "Node deleted",
      description: "The node has been removed from the flow",
    });
  }, [selectedNode, setNodes, setEdges, toast]);

  // Keyboard shortcuts for delete, undo, redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Delete key - delete selected node
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNode) {
        e.preventDefault();
        deleteNode();
      }

      // Ctrl+Z - Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Ctrl+Y or Ctrl+Shift+Z - Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, deleteNode, undo, redo]);

  // Get available nodes for dropdowns
  const getAvailableNodes = () => {
    return nodes.map((node) => ({
      id: node.id,
      label: `${node.data.label} (${node.id.substring(node.id.length - 8)})`,
    }));
  };

  return (
    <div className="flex h-full w-full">
      {/* Sidebar - Node Types with Teal Theme */}
      <div className="w-52 xl:w-60 flex-shrink-0 flex flex-col border-r bg-gradient-to-b from-teal-50/50 via-cyan-50/30 to-background dark:from-teal-950/30 dark:via-cyan-950/20 dark:to-background">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-teal-100 dark:border-teal-900/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/app/flows")}
            className="mb-3 -ml-2"
            data-testid="button-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("flows.backToFlows")}
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/25">
              <GitBranch className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3
                className="font-semibold text-sm text-teal-900 dark:text-teal-100"
                data-testid="text-node-types-title"
              >
                {t("flows.title")}
              </h3>
              <p className="text-xs text-teal-600/70 dark:text-teal-400/70">
                {t("flows.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* AI Edit Visual Flow Button - always visible for chat/screenshot functionality */}
        <div className="p-4 border-b border-teal-100 dark:border-teal-900/50 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-950/30 dark:to-indigo-950/30">
          <Button
            size="sm"
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-purple-500/25"
            onClick={() => setVisualEditorOpen(true)}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Flow Editor
          </Button>
        </div>

        {/* Node Types */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-xs font-medium text-teal-700 dark:text-teal-300 mb-3 uppercase tracking-wider">
            {t("flows.addNodes")}
          </div>
          <div className="space-y-2">
            <TooltipProvider delayDuration={200}>
              {availableNodeTypes.map((nodeType) => {
                const Icon = nodeType.icon;
                const colors =
                  nodeTypeColors[nodeType.type as keyof typeof nodeTypeColors];
                return (
                  <div key={nodeType.type} className="relative">
                    {/* Help tooltip icon - positioned at top right corner */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 z-10 p-1 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 hover:bg-teal-50 dark:hover:bg-teal-900/50 text-muted-foreground hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Info className="w-3 h-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[280px] p-3">
                        <p className="font-semibold text-sm mb-1.5">{nodeType.label}</p>
                        <p className="text-xs leading-relaxed">{nodeType.help}</p>
                      </TooltipContent>
                    </Tooltip>
                    {/* Node button - full width */}
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2 h-auto py-2.5 hover-elevate active-elevate-2 border-l-4 bg-white/50 dark:bg-white/5"
                      style={{ borderLeftColor: colors.handle }}
                      onClick={() => addNode(nodeType.type)}
                      data-testid={`button-add-${nodeType.type}-node`}
                    >
                      <div
                        className={`p-1.5 rounded bg-gradient-to-br ${colors.bg}`}
                      >
                        <Icon className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium text-sm">{nodeType.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {nodeType.description}
                        </div>
                      </div>
                      <Plus className="w-4 h-4 flex-shrink-0 text-teal-500" />
                    </Button>
                  </div>
                );
              })}
            </TooltipProvider>
          </div>
        </div>

        {/* Sidebar Footer Tip */}
        <div className="p-4 border-t border-teal-100 dark:border-teal-900/50 bg-teal-50/50 dark:bg-teal-950/30 space-y-2">
          <p className="text-xs font-medium text-teal-700 dark:text-teal-300">
            🔗 Click to connect nodes:
          </p>
          <ol className="text-xs text-teal-600 dark:text-teal-400 space-y-1 list-decimal list-inside">
            <li>Click a <strong>bottom</strong> circle (source)</li>
            <li>Click a <strong>top</strong> circle (target)</li>
          </ol>
          <p className="text-xs text-teal-600/80 dark:text-teal-400/80 mt-2 bg-teal-100/50 dark:bg-teal-900/30 p-2 rounded">
            💡 Selected handle glows teal. Press <kbd className="px-1 py-0.5 bg-white dark:bg-gray-800 rounded text-[10px]">Esc</kbd> or click empty area to cancel.
          </p>
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col">
        {/* Top Toolbar with Teal Theme */}
        <div className="border-b px-4 py-3 flex items-center justify-between gap-4 bg-gradient-to-r from-teal-50/80 via-cyan-50/50 to-background dark:from-teal-950/40 dark:via-cyan-950/30 dark:to-background">
          <div className="flex-1">
            <input
              type="text"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              className="text-lg font-semibold bg-transparent border-none outline-none w-full"
              placeholder={t("flows.flowName")}
              data-testid="input-flow-name"
            />
            <input
              type="text"
              value={flowDescription}
              onChange={(e) => setFlowDescription(e.target.value)}
              className="text-sm text-muted-foreground bg-transparent border-none outline-none w-full mt-1"
              placeholder={t("flows.flowDescriptionPlaceholder")}
              data-testid="input-flow-description"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Reading the flow as a script is the other half of editing it: the
                canvas shows the wiring, this shows what the caller hears. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setScriptOpen(true)}
              data-testid="button-view-script"
            >
              <FileText className="w-4 h-4 mr-2" />
              Script
            </Button>
            <Button
              size="sm"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white shadow-md shadow-teal-500/25"
              data-testid="button-save-flow"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? t("flows.saving") : t("flows.save")}
            </Button>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div className="flex-1 flex flex-col relative">
          {/* Main Canvas Area - shrinks when AI chat is open */}
          <div className={`${visualEditorOpen ? 'flex-1' : 'flex-1'} relative`} style={{ minHeight: visualEditorOpen ? '60%' : '100%' }}>
          <ReactFlowProvider>
          <ConnectionContext.Provider value={{ pendingConnection, setPendingConnection, onHandleClick }}>
            <ReactFlow
              key={`main-canvas-${flowId || 'new'}`}
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onSelectionChange={onSelectionChange}
              onNodeDragStart={onNodeDragStart}
              nodesDraggable={true}
              nodesConnectable={true}
              elementsSelectable={true}
              selectNodesOnDrag={false}
              onPaneClick={() => {
                // Cancel pending connection when clicking on empty canvas
                if (pendingConnection) {
                  setPendingConnection(null);
                }
                // Clear selected node when clicking empty canvas
                setSelectedNode(null);
              }}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              snapToGrid
              snapGrid={[15, 15]}
              connectionRadius={80}
              connectOnClick={true}
              connectionMode={ConnectionMode.Loose}
              elevateEdgesOnSelect={true}
              edgesFocusable={true}
              defaultEdgeOptions={{
                animated: true,
                style: {
                  strokeWidth: 3,
                  stroke: "url(#edge-gradient)",
                },
              }}
              connectionLineStyle={{
                strokeWidth: 4,
                stroke: "#14b8a6",
                strokeDasharray: "8 4",
              }}
              data-testid="flow-canvas"
            >
            <svg width="0" height="0">
              <defs>
                <linearGradient
                  id="edge-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#14b8a6" />
                  <stop offset="50%" stopColor="#06b6d4" />
                  <stop offset="100%" stopColor="#0891b2" />
                </linearGradient>
              </defs>
            </svg>
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1.5}
              className="bg-gradient-to-br from-teal-50/30 via-cyan-50/30 to-white dark:from-teal-950/20 dark:via-cyan-950/20 dark:to-background"
            />
            <Controls className="!bg-background/90 !backdrop-blur !border !border-border !shadow-lg" />
            {/* Connection Status Indicator */}
            {pendingConnection && (
              <Panel position="top-center">
                <div className="bg-teal-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                  <div className="w-2 h-2 bg-white rounded-full" />
                  <span className="text-sm font-medium">
                    {pendingConnection.handleType === 'source'
                      ? 'Now click a TOP circle to connect'
                      : 'Now click a BOTTOM circle to connect'}
                  </span>
                  <button
                    onClick={() => setPendingConnection(null)}
                    className="ml-2 hover:bg-teal-600 rounded p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </Panel>
            )}
            <MiniMap
              className="!bg-background/90 !backdrop-blur !border !border-border !shadow-lg"
              nodeColor={(node) => {
                const type = node.data?.type as keyof typeof nodeTypeColors;
                const colors = nodeTypeColors[type];
                if (colors) {
                  return colors.handle;
                }
                return "hsl(var(--primary))";
              }}
              maskColor="rgba(0, 0, 0, 0.1)"
            />

            {/* Outbound Compatibility Validation Panel */}
            {flowId && outboundValidation && (
              <Panel position="top-right" className="mr-2 mt-2">
                <div className="bg-background/95 backdrop-blur border border-border rounded-lg shadow-lg p-3 max-w-xs">
                  <div className="flex items-center gap-2 mb-2">
                    {outboundValidation.isCompatible ? (
                      outboundValidation.compatibilityLevel === 'fully_compatible' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      )
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="text-sm font-medium">
                      {outboundValidation.compatibilityLevel === 'fully_compatible' && 'Outbound Ready'}
                      {outboundValidation.compatibilityLevel === 'requires_agent_config' && 'Needs Config'}
                      {outboundValidation.compatibilityLevel === 'incompatible' && 'Not Compatible'}
                      {outboundValidation.compatibilityLevel === 'unknown' && 'Validating...'}
                    </span>
                  </div>

                  {/* Show errors */}
                  {outboundValidation.issues.filter(i => i.severity === 'error').length > 0 && (
                    <div className="space-y-1 mt-2 border-t pt-2">
                      <span className="text-xs font-medium text-red-600 dark:text-red-400">Issues:</span>
                      {outboundValidation.issues
                        .filter(i => i.severity === 'error')
                        .slice(0, 3)
                        .map((issue, idx) => (
                          <div key={idx} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                            <span>•</span>
                            <span>{issue.message}</span>
                          </div>
                        ))}
                      {outboundValidation.issues.filter(i => i.severity === 'error').length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{outboundValidation.issues.filter(i => i.severity === 'error').length - 3} more
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show requirements for requires_agent_config */}
                  {outboundValidation.compatibilityLevel === 'requires_agent_config' && (
                    <div className="space-y-1 mt-2 border-t pt-2">
                      <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Agent config needed:</span>
                      {outboundValidation.requirements.needsTransferNumber && (
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">• Transfer phone number</div>
                      )}
                      {outboundValidation.requirements.needsHostEmail && (
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">• Meeting host email</div>
                      )}
                      {outboundValidation.requirements.needsCalendarConnection && (
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">• Calendar connection</div>
                      )}
                    </div>
                  )}

                  {/* Refresh button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refetchValidation()}
                    className="w-full mt-2 h-7 text-xs"
                  >
                    <Loader2 className="w-3 h-3 mr-1" />
                    Re-validate
                  </Button>
                </div>
              </Panel>
            )}
          </ReactFlow>
          </ConnectionContext.Provider>
          </ReactFlowProvider>
          </div>

          {/* Inline AI Chat Panel - appears when AI Flow Editor is clicked */}
          {visualEditorOpen && (
            <div className="flex-shrink-0 border-t bg-gradient-to-r from-purple-50/80 to-indigo-50/80 dark:from-purple-950/30 dark:to-indigo-950/30">
              {/* Chat Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b bg-white/50 dark:bg-gray-900/50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-medium">AI Flow Editor</span>
                  {chatMessages.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({chatMessages.filter(m => m.role === 'user').length} prompts)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Analyze Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={analyzeFlow}
                    disabled={isAnalyzing || nodes.length === 0}
                    className="h-7 px-2 text-xs bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-700 dark:bg-amber-950 dark:hover:bg-amber-900 dark:border-amber-800 dark:text-amber-300"
                  >
                    {isAnalyzing ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-3 h-3 mr-1" />
                    )}
                    Analyze
                  </Button>
                  {/* Fix All Button - only shows when issues detected */}
                  {flowAnalysis?.hasIssues && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={fixAllIssues}
                      disabled={isProcessingChat}
                      className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
                    >
                      {isProcessingChat ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                      )}
                      Fix All ({flowAnalysis.issues.length})
                    </Button>
                  )}
                  {chatMessages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setChatMessages([]);
                        setChatInput("");
                        setFlowAnalysis(null);
                        setChatImage(null);
                        // Clear from localStorage
                        if (chatStorageKey) {
                          localStorage.removeItem(chatStorageKey);
                        }
                      }}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                      title="Clear conversation history"
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setVisualEditorOpen(false);
                      setChatInput("");
                      setChatImage(null);
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Autopilot Suggestions - shown when no chat messages yet */}
              {chatMessages.length === 0 && nodes.length > 0 && (
                <div className="px-3 py-2 border-b bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Autopilot Suggestions</span>
                    {isLoadingSuggestions && (
                      <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
                    )}
                  </div>
                  {isLoadingSuggestions ? (
                    <div className="flex gap-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-8 bg-amber-100/50 dark:bg-amber-900/20 rounded-full animate-pulse flex-1"
                        />
                      ))}
                    </div>
                  ) : autopilotSuggestions.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {autopilotSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          onClick={() => {
                            // Apply suggestion by sending it to the chat
                            setChatInput(suggestion.prompt);
                            // Auto-submit after a brief delay
                            setTimeout(() => {
                              const userMessage = {
                                role: 'user' as const,
                                content: suggestion.prompt,
                                timestamp: new Date(),
                              };
                              setChatMessages(prev => [...prev, userMessage]);
                              setChatInput("");
                              setIsProcessingChat(true);
                              // Remove this suggestion from the list
                              setAutopilotSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
                              // Process the edit
                              apiRequest("POST", "/api/flow-automation/flows/edit", {
                                prompt: suggestion.prompt,
                                nodes: nodes,
                                edges: edges,
                              }).then(async (response) => {
                                const data = await response.json();
                                if (data.success && data.nodes && data.edges) {
                                  setNodes(data.nodes);
                                  setEdges(data.edges);
                                  setChatMessages(prev => [...prev, {
                                    role: 'assistant',
                                    content: `✅ ${data.summary || 'Changes applied successfully!'}`,
                                    timestamp: new Date(),
                                  }]);
                                  toast({
                                    title: "Suggestion Applied",
                                    description: suggestion.text,
                                  });
                                } else {
                                  setChatMessages(prev => [...prev, {
                                    role: 'assistant',
                                    content: `❌ ${data.error || 'Could not apply suggestion. Try rephrasing.'}`,
                                    timestamp: new Date(),
                                  }]);
                                }
                              }).catch((error) => {
                                setChatMessages(prev => [...prev, {
                                  role: 'assistant',
                                  content: `❌ Error: ${error.message}`,
                                  timestamp: new Date(),
                                }]);
                              }).finally(() => {
                                setIsProcessingChat(false);
                              });
                            }, 100);
                          }}
                          disabled={isProcessingChat}
                          className={`
                            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                            transition-all duration-200 hover:scale-105
                            ${suggestion.priority === 'high'
                              ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50'
                              : suggestion.priority === 'medium'
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                            }
                            ${isProcessingChat ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          `}
                          title={suggestion.prompt}
                        >
                          {suggestion.priority === 'high' && <ArrowUp className="w-3 h-3" />}
                          {suggestion.text}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      Type a command below or click Analyze to find issues
                    </p>
                  )}
                </div>
              )}

              {/* Chat Messages */}
              {chatMessages.length > 0 && (
                <div
                  ref={chatContainerRef}
                  className="max-h-[120px] overflow-y-auto p-3 space-y-2"
                >
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-1.5 ${
                          msg.role === 'user'
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-foreground shadow-sm'
                        }`}
                      >
                        {msg.image && (
                          <img src={msg.image} alt="Attached" className="max-h-16 rounded mb-1" />
                        )}
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {isProcessingChat && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-1.5 shadow-sm">
                        <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Chat Input */}
              <div className="p-3 bg-white/80 dark:bg-gray-900/80">
                {chatImage && (
                  <div className="mb-2 relative inline-block">
                    <img src={chatImage} alt="Attached" className="h-10 rounded border" />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-5 w-5 p-0 rounded-full"
                      onClick={() => setChatImage(null)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 items-end">
                  <input
                    ref={chatImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleChatImageUpload}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => chatImageInputRef.current?.click()}
                    disabled={isProcessingChat}
                    title="Attach screenshot"
                    className="h-[60px] w-10 self-end"
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                  <Textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Describe changes... e.g., 'Add a greeting node', 'Delete the last node', 'Connect node A to node B'"
                    disabled={isProcessingChat}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        processInlineChatMessage();
                      }
                    }}
                    className="flex-1 min-h-[60px] max-h-[100px] resize-none text-sm"
                    rows={2}
                  />
                  <Button
                    size="icon"
                    onClick={processInlineChatMessage}
                    disabled={isProcessingChat || (!chatInput.trim() && !chatImage)}
                    className="bg-purple-500 hover:bg-purple-600 h-[60px] w-10 self-end"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Properties Panel (Right Sidebar) with Teal Theme */}
          {selectedNode && (
            <div className="absolute right-0 top-0 bottom-0 w-72 bg-gradient-to-b from-teal-50/30 via-background to-background dark:from-teal-950/20 dark:via-background border-l shadow-lg overflow-y-auto z-10">
              <div className="p-4 border-b border-teal-100 dark:border-teal-900/50 flex items-center justify-between sticky top-0 bg-gradient-to-r from-teal-50/80 to-cyan-50/50 dark:from-teal-950/50 dark:to-cyan-950/30 backdrop-blur z-10">
                <div className="flex items-center gap-2">
                  {(() => {
                    const Icon =
                      nodeTypeIcons[
                        selectedNode.data.type as keyof typeof nodeTypeIcons
                      ] || MessageSquare;
                    return (
                      <Icon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    );
                  })()}
                  <h3 className="font-semibold text-sm text-teal-900 dark:text-teal-100">
                    {t("flows.nodeProperties")}
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 hover:bg-destructive/10"
                          onClick={deleteNode}
                          data-testid="button-delete-node"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Delete node (Delete key)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setSelectedNode(null)}
                    data-testid="button-close-properties"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Node ID (read-only reference) */}
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {t("flows.nodeId")}
                  </Label>
                  <div className="text-sm font-mono bg-muted/50 px-2 py-1.5 rounded mt-1 break-all">
                    {selectedNode.id}
                  </div>
                </div>

                {/* Message Node */}
                {selectedNode.data.type === "message" && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="message">
                          {t("flows.nodeConfig.message")}
                        </Label>
                        <div className="flex items-center gap-1">
                          <PlaceholderHelper compact />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                            onClick={() => {
                              setGeneratingField("message");
                              generateContentMutation.mutate({
                                nodeType: "message",
                                fieldType: "message",
                                context: selectedNode.data.label || "greeting message",
                              });
                            }}
                            disabled={generatingField === "message"}
                          >
                            {generatingField === "message" ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Wand2 className="w-3.5 h-3.5 mr-1" />
                            )}
                            AI Assist
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        id="message"
                        value={selectedNode.data.config?.message || ""}
                        onChange={(e) =>
                          updateNodeConfig({ message: e.target.value })
                        }
                        placeholder="Hi {{first_name}}, thank you for taking my call..."
                        data-testid="input-message"
                      />
                      <PlaceholderHint />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="waitForResponse"
                        checked={
                          selectedNode.data.config?.waitForResponse ?? false
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ waitForResponse: !!checked })
                        }
                        data-testid="checkbox-wait-for-response"
                      />
                      <Label
                        htmlFor="waitForResponse"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.waitForResponse")}
                      </Label>
                    </div>
                  </div>
                )}

                {/* Question Node */}
                {selectedNode.data.type === "question" && (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="question">
                          {t("flows.nodeConfig.question")}
                        </Label>
                        <div className="flex items-center gap-1">
                          <PlaceholderHelper compact />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                            onClick={() => {
                              setGeneratingField("question");
                              generateContentMutation.mutate({
                                nodeType: "question",
                                fieldType: "question",
                                context: selectedNode.data.label || "collecting caller information",
                              });
                            }}
                            disabled={generatingField === "question"}
                          >
                            {generatingField === "question" ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Wand2 className="w-3.5 h-3.5 mr-1" />
                            )}
                            AI Assist
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        id="question"
                        value={selectedNode.data.config?.question || ""}
                        onChange={(e) =>
                          updateNodeConfig({ question: e.target.value })
                        }
                        placeholder="{{first_name}}, may I know your preferred time for a callback?"
                        data-testid="input-question"
                      />
                      <PlaceholderHint />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Label htmlFor="variableName">
                          {t("flows.nodeConfig.variableName")}
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">
                                {t("flows.nodeConfig.variableTooltip")}
                              </p>
                              <div className="mt-2 p-2 bg-muted rounded text-xs font-mono">
                                {t("flows.nodeConfig.variableExample")}
                                <br />
                                Variable: transfer_consent
                                <br />
                                Use in condition: transfer_consent == "yes"
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input
                        id="variableName"
                        value={selectedNode.data.config?.variableName || ""}
                        onChange={(e) =>
                          updateNodeConfig({ variableName: e.target.value })
                        }
                        placeholder={t(
                          "flows.nodeConfig.variableNamePlaceholder"
                        )}
                        className="mt-1"
                        data-testid="input-variable-name"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("flows.nodeConfig.variableNameHint")}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="waitForResponseQuestion"
                        checked={
                          selectedNode.data.config?.waitForResponse ?? true
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ waitForResponse: !!checked })
                        }
                        data-testid="checkbox-wait-for-response"
                      />
                      <Label
                        htmlFor="waitForResponseQuestion"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.waitForResponse")}
                      </Label>
                    </div>

                    {/* Response Branching Section */}
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GitBranch className="w-4 h-4 text-purple-500" />
                          <Label className="text-sm font-medium">Response Branching</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="enableBranching"
                            checked={selectedNode.data.config?.enableBranching ?? false}
                            onCheckedChange={(checked) => {
                              updateNodeConfig({ enableBranching: !!checked });
                              // Initialize with default options if enabling
                              if (checked && !selectedNode.data.config?.responseOptions?.length) {
                                updateNodeConfig({
                                  enableBranching: true,
                                  responseOptions: [
                                    { id: `resp-${Date.now()}-yes`, label: 'Yes', keywords: ['yes', 'sure', 'okay', 'definitely', 'absolutely'] },
                                    { id: `resp-${Date.now()}-no`, label: 'No', keywords: ['no', 'not', 'nope', 'never'] },
                                  ]
                                });
                              }
                            }}
                          />
                          <Label htmlFor="enableBranching" className="text-xs cursor-pointer">
                            Enable
                          </Label>
                        </div>
                      </div>

                      {selectedNode.data.config?.enableBranching && (
                        <>
                          <p className="text-xs text-muted-foreground mb-3">
                            Define how responses branch to different paths. Each option creates a separate output handle.
                          </p>

                          {/* Response Options List */}
                          <div className="space-y-2">
                            {(selectedNode.data.config?.responseOptions || []).map((option: ResponseOption, index: number) => (
                              <div key={option.id} className="p-2 border rounded-md bg-muted/30">
                                <div className="flex items-center gap-2 mb-2">
                                  <Input
                                    value={option.label}
                                    onChange={(e) => {
                                      const newOptions = [...(selectedNode.data.config?.responseOptions || [])];
                                      newOptions[index] = { ...option, label: e.target.value };
                                      updateNodeConfig({ responseOptions: newOptions });
                                    }}
                                    placeholder="Label (e.g., Yes)"
                                    className="h-8 text-sm flex-1"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => {
                                      const newOptions = (selectedNode.data.config?.responseOptions || []).filter((_: any, i: number) => i !== index);
                                      updateNodeConfig({ responseOptions: newOptions });
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                                <Input
                                  value={option.keywords?.join(', ') || ''}
                                  onChange={(e) => {
                                    const newOptions = [...(selectedNode.data.config?.responseOptions || [])];
                                    newOptions[index] = {
                                      ...option,
                                      keywords: e.target.value.split(',').map((k: string) => k.trim()).filter((k: string) => k)
                                    };
                                    updateNodeConfig({ responseOptions: newOptions });
                                  }}
                                  placeholder="Keywords (comma-separated): yes, sure, okay"
                                  className="h-7 text-xs"
                                />
                              </div>
                            ))}
                          </div>

                          {/* Add Response Option Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2 h-8"
                            onClick={() => {
                              const newOption: ResponseOption = {
                                id: `resp-${Date.now()}`,
                                label: '',
                                keywords: []
                              };
                              const currentOptions = selectedNode.data.config?.responseOptions || [];
                              updateNodeConfig({ responseOptions: [...currentOptions, newOption] });
                            }}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Add Response Option
                          </Button>

                          {/* Quick Presets */}
                          <div className="mt-3">
                            <Label className="text-xs text-muted-foreground">Quick Presets:</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[10px] px-2"
                                onClick={() => {
                                  updateNodeConfig({
                                    responseOptions: [
                                      { id: `resp-${Date.now()}-yes`, label: 'Yes', keywords: ['yes', 'sure', 'okay', 'definitely', 'absolutely', 'yeah'] },
                                      { id: `resp-${Date.now()}-no`, label: 'No', keywords: ['no', 'not', 'nope', 'never', 'nah'] },
                                    ]
                                  });
                                }}
                              >
                                Yes / No
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[10px] px-2"
                                onClick={() => {
                                  updateNodeConfig({
                                    responseOptions: [
                                      { id: `resp-${Date.now()}-interested`, label: 'Interested', keywords: ['interested', 'yes', 'tell me more', 'sure', 'sounds good'] },
                                      { id: `resp-${Date.now()}-not-interested`, label: 'Not Interested', keywords: ['not interested', 'no', 'busy', 'not now'] },
                                      { id: `resp-${Date.now()}-callback`, label: 'Callback', keywords: ['call back', 'later', 'another time', 'busy now'] },
                                    ]
                                  });
                                }}
                              >
                                Interest Level
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[10px] px-2"
                                onClick={() => {
                                  updateNodeConfig({
                                    responseOptions: [
                                      { id: `resp-${Date.now()}-confirm`, label: 'Confirm', keywords: ['yes', 'correct', 'right', 'confirm', 'that\'s right'] },
                                      { id: `resp-${Date.now()}-deny`, label: 'Deny', keywords: ['no', 'wrong', 'incorrect', 'not right'] },
                                      { id: `resp-${Date.now()}-unsure`, label: 'Unsure', keywords: ['not sure', 'maybe', 'i think', 'possibly'] },
                                    ]
                                  });
                                }}
                              >
                                Confirmation
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {/* Condition Node with Smart Node Selector */}
                {selectedNode.data.type === "condition" && (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="condition">
                            {t("flows.nodeConfig.condition")}
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs mb-2">
                                  {t("flows.nodeConfig.conditionTooltip")}
                                </p>
                                <div className="space-y-1 text-xs font-mono bg-muted p-2 rounded">
                                  <div>transfer_consent == "yes"</div>
                                  <div>age &gt; 18</div>
                                  <div>response contains "help"</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                          onClick={() => {
                            setGeneratingField("condition");
                            generateContentMutation.mutate({
                              nodeType: "condition",
                              fieldType: "condition",
                              context: selectedNode.data.label || "branching based on caller response",
                            });
                          }}
                          disabled={generatingField === "condition"}
                        >
                          {generatingField === "condition" ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <Wand2 className="w-3.5 h-3.5 mr-1" />
                          )}
                          AI Assist
                        </Button>
                      </div>
                      <Textarea
                        id="condition"
                        value={selectedNode.data.config?.condition || ""}
                        onChange={(e) =>
                          updateNodeConfig({ condition: e.target.value })
                        }
                        placeholder={t("flows.nodeConfig.conditionPlaceholder")}
                        data-testid="input-condition"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("flows.nodeConfig.conditionHint")}
                      </p>
                    </div>

                    {/* True Branch - Node Selector */}
                    <div>
                      <Label htmlFor="trueBranch">
                        {t("flows.nodeConfig.trueBranch")}
                      </Label>
                      <Select
                        value={selectedNode.data.config?.trueBranch || ""}
                        onValueChange={(value) =>
                          updateNodeConfig({ trueBranch: value })
                        }
                      >
                        <SelectTrigger
                          className="mt-1"
                          data-testid="select-true-branch"
                        >
                          <SelectValue
                            placeholder={t("flows.nodeConfig.selectNextNode")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableNodes()
                            .filter((n) => n.id !== selectedNode.id)
                            .map((node) => (
                              <SelectItem key={node.id} value={node.id}>
                                {node.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* False Branch - Node Selector */}
                    <div>
                      <Label htmlFor="falseBranch">
                        {t("flows.nodeConfig.falseBranch")}
                      </Label>
                      <Select
                        value={selectedNode.data.config?.falseBranch || ""}
                        onValueChange={(value) =>
                          updateNodeConfig({ falseBranch: value })
                        }
                      >
                        <SelectTrigger
                          className="mt-1"
                          data-testid="select-false-branch"
                        >
                          <SelectValue
                            placeholder={t("flows.nodeConfig.selectNextNode")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableNodes()
                            .filter((n) => n.id !== selectedNode.id)
                            .map((node) => (
                              <SelectItem key={node.id} value={node.id}>
                                {node.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {/* Transfer Node */}
                {selectedNode.data.type === "transfer" && (
                  <div>
                    <Label htmlFor="phoneNumber">
                      {t("flows.nodeConfig.phoneNumber")}
                    </Label>
                    <Input
                      id="phoneNumber"
                      value={selectedNode.data.config?.phoneNumber || ""}
                      onChange={(e) =>
                        updateNodeConfig({ phoneNumber: e.target.value })
                      }
                      placeholder={t("flows.nodeConfig.phoneNumberPlaceholder")}
                      className="mt-1"
                      data-testid="input-phone-number"
                    />
                  </div>
                )}

                {/* Delay Node */}
                {selectedNode.data.type === "delay" && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="duration">
                        {t("flows.nodeConfig.duration")}
                      </Label>
                      <Input
                        id="duration"
                        type="number"
                        value={selectedNode.data.config?.duration || 1}
                        onChange={(e) =>
                          updateNodeConfig({
                            duration: parseInt(e.target.value),
                          })
                        }
                        placeholder={t("flows.nodeConfig.durationPlaceholder")}
                        min="1"
                        className="mt-1"
                        data-testid="input-duration"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="waitForResponseDelay"
                        checked={
                          selectedNode.data.config?.waitForResponse ?? false
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ waitForResponse: !!checked })
                        }
                        data-testid="checkbox-wait-for-response"
                      />
                      <Label
                        htmlFor="waitForResponseDelay"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.waitForResponse")}
                      </Label>
                    </div>
                  </div>
                )}

                {/* Callback Node */}
                {selectedNode.data.type === "callback" && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="askForTime"
                        checked={
                          selectedNode.data.config?.askForTime ?? true
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ askForTime: !!checked })
                        }
                        data-testid="checkbox-ask-for-time"
                      />
                      <Label
                        htmlFor="askForTime"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.askForTime", "Ask for preferred callback time")}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2 ml-6">
                      {t("flows.nodeConfig.askForTimeHint", "When enabled, the AI will ask when the customer would like to be called back")}
                    </p>

                    {!(selectedNode.data.config?.askForTime ?? true) && (
                      <div>
                        <Label htmlFor="defaultDelayHours">
                          {t("flows.nodeConfig.defaultDelayHours", "Default Callback Delay (hours)")}
                        </Label>
                        <Input
                          id="defaultDelayHours"
                          type="number"
                          min={1}
                          max={168}
                          value={selectedNode.data.config?.defaultDelayHours || 24}
                          onChange={(e) =>
                            updateNodeConfig({
                              defaultDelayHours: parseInt(e.target.value) || 24,
                            })
                          }
                          placeholder="24"
                          className="mt-1"
                          data-testid="input-default-delay"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("flows.nodeConfig.defaultDelayHint", "Callback will be scheduled this many hours from now")}
                        </p>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="confirmationMessage">
                        {t("flows.nodeConfig.confirmationMessage", "Confirmation Message")}
                      </Label>
                      <Textarea
                        id="confirmationMessage"
                        value={selectedNode.data.config?.confirmationMessage || "I'll call you back at the scheduled time. Have a great day!"}
                        onChange={(e) =>
                          updateNodeConfig({ confirmationMessage: e.target.value })
                        }
                        placeholder={t("flows.nodeConfig.confirmationMessagePlaceholder", "I'll call you back at the scheduled time. Have a great day!")}
                        className="mt-1"
                        rows={2}
                        data-testid="input-confirmation-message"
                      />
                    </div>

                    <div>
                      <Label htmlFor="maxCallbackAttempts">
                        {t("flows.nodeConfig.maxCallbackAttempts", "Max Callback Attempts")}
                      </Label>
                      <Input
                        id="maxCallbackAttempts"
                        type="number"
                        min={1}
                        max={10}
                        value={selectedNode.data.config?.maxCallbackAttempts || 3}
                        onChange={(e) =>
                          updateNodeConfig({
                            maxCallbackAttempts: parseInt(e.target.value) || 3,
                          })
                        }
                        placeholder="3"
                        className="mt-1"
                        data-testid="input-max-attempts"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("flows.nodeConfig.maxCallbackAttemptsHint", "Number of times the system will attempt to call back")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Webhook Node */}
                {selectedNode.data.type === "webhook" && (
                  <div>
                    <Label htmlFor="webhookUrl">
                      {t("flows.nodeConfig.webhookUrl")}
                    </Label>
                    <Input
                      id="webhookUrl"
                      value={selectedNode.data.config?.webhookUrl || ""}
                      onChange={(e) =>
                        updateNodeConfig({ webhookUrl: e.target.value })
                      }
                      placeholder={t("flows.nodeConfig.webhookUrlPlaceholder")}
                      className="mt-1"
                      data-testid="input-webhook-url"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("flows.nodeConfig.webhookHint")}
                    </p>
                  </div>
                )}

                {/* Form Node */}
                {selectedNode.data.type === "form" && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="formId">
                        {t("flows.nodeConfig.selectForm")}
                      </Label>
                      <Select
                        value={selectedNode.data.config?.formId || ""}
                        onValueChange={(value) => {
                          const selectedForm = availableForms?.find(
                            (f) => f.id === value
                          );
                          updateNodeConfig({
                            formId: value,
                            formName: selectedForm?.name || "",
                          });
                        }}
                      >
                        <SelectTrigger
                          className="mt-1"
                          data-testid="select-form"
                        >
                          <SelectValue
                            placeholder={t(
                              "flows.nodeConfig.selectFormPlaceholder"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableForms?.map((form) => (
                            <SelectItem key={form.id} value={form.id}>
                              {form.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(!availableForms || availableForms.length === 0) && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          {t("flows.nodeConfig.noFormsAvailable")}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label htmlFor="formIntroMessage">
                          {t("flows.nodeConfig.introMessage")}
                        </Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                          onClick={() => {
                            setGeneratingField("formMessage");
                            generateContentMutation.mutate({
                              nodeType: "form",
                              fieldType: "message",
                              context: selectedNode.data.label || "introducing form to collect information",
                            });
                          }}
                          disabled={generatingField === "formMessage"}
                        >
                          {generatingField === "formMessage" ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <Wand2 className="w-3.5 h-3.5 mr-1" />
                          )}
                          AI Assist
                        </Button>
                      </div>
                      <Textarea
                        id="formIntroMessage"
                        value={selectedNode.data.config?.message || ""}
                        onChange={(e) =>
                          updateNodeConfig({ message: e.target.value })
                        }
                        placeholder={t("flows.nodeConfig.formIntroPlaceholder")}
                        rows={2}
                        data-testid="input-form-intro"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("flows.nodeConfig.formIntroHint")}
                      </p>
                    </div>

                    {selectedNode.data.config?.formId && (
                      <div className="p-3 bg-muted/50 rounded-md">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          {t("flows.nodeConfig.formFieldsPreview")}
                        </p>
                        {(() => {
                          // Try to get fields from selectedFormDetails first (most reliable)
                          const formFields =
                            selectedFormDetails?.fields ||
                            availableForms?.find(
                              (f) => f.id === selectedNode.data.config?.formId
                            )?.fields;

                          if (isLoadingFormDetails) {
                            return (
                              <p className="text-xs text-muted-foreground italic">
                                {t("flows.nodeConfig.loadingFields")}
                              </p>
                            );
                          }

                          if (formDetailsError) {
                            return (
                              <p className="text-xs text-destructive italic">
                                Failed to load form fields. Please try
                                refreshing.
                              </p>
                            );
                          }

                          if (
                            formFields &&
                            Array.isArray(formFields) &&
                            formFields.length > 0
                          ) {
                            return (
                              <ul className="text-xs space-y-1">
                                {formFields
                                  .sort(
                                    (a, b) => (a.order || 0) - (b.order || 0)
                                  )
                                  .map((field, idx) => (
                                    <li
                                      key={field.id}
                                      className="flex items-center gap-2"
                                    >
                                      <span className="text-muted-foreground">
                                        {idx + 1}.
                                      </span>
                                      <span>{field.question}</span>
                                      {field.isRequired && (
                                        <span className="text-amber-600 dark:text-amber-400">
                                          *
                                        </span>
                                      )}
                                    </li>
                                  ))}
                              </ul>
                            );
                          }

                          if (
                            formFields &&
                            Array.isArray(formFields) &&
                            formFields.length === 0
                          ) {
                            return (
                              <p className="text-xs text-muted-foreground italic">
                                No fields configured for this form
                              </p>
                            );
                          }

                          return (
                            <p className="text-xs text-muted-foreground italic">
                              {t("flows.nodeConfig.loadingFields")}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="waitForResponseForm"
                        checked={
                          selectedNode.data.config?.waitForResponse ?? true
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ waitForResponse: !!checked })
                        }
                        data-testid="checkbox-wait-for-response"
                      />
                      <Label
                        htmlFor="waitForResponseForm"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.waitForResponse")}
                      </Label>
                    </div>
                  </div>
                )}

                {/* Appointment Node */}
                {selectedNode.data.type === "appointment" && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="appointmentType">
                        {t("flows.nodeConfig.appointmentType")}
                      </Label>
                      <Input
                        id="appointmentType"
                        value={selectedNode.data.config?.appointmentType || ""}
                        onChange={(e) =>
                          updateNodeConfig({ appointmentType: e.target.value })
                        }
                        placeholder={t(
                          "flows.nodeConfig.appointmentTypePlaceholder"
                        )}
                        className="mt-1"
                        data-testid="input-appointment-type"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("flows.nodeConfig.appointmentHint")}
                      </p>
                    </div>

                    {/* Host Calendar Selection */}
                    <div>
                      <Label htmlFor="hostCalendar">
                        Host Calendar
                      </Label>
                      <Select
                        value={selectedNode.data.config?.hostEmail || "default"}
                        onValueChange={(value) =>
                          updateNodeConfig({ hostEmail: value === "default" ? undefined : value })
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select calendar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">
                            <span className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              Default (from settings)
                            </span>
                          </SelectItem>
                          {connectedCalendars?.calendars?.map((cal) => (
                            <SelectItem key={cal.email} value={cal.email}>
                              <span className="flex items-center gap-2">
                                {cal.provider === 'google' ? (
                                  <span className="text-red-500 text-xs font-medium">G</span>
                                ) : (
                                  <span className="text-blue-500 text-xs font-medium">M</span>
                                )}
                                {cal.email}
                                {cal.isPrimary && (
                                  <span className="text-xs text-muted-foreground">(Primary)</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(!connectedCalendars?.calendars || connectedCalendars.calendars.length === 0) ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          No calendars connected. Go to <strong>Appointments → Settings</strong> to connect Google or Microsoft calendar.
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          Select which calendar to book appointments on
                        </p>
                      )}
                    </div>

                    {/* Additional Attendees */}
                    <div>
                      <Label htmlFor="attendeeEmails">
                        Additional Attendees
                      </Label>
                      <Input
                        id="attendeeEmails"
                        placeholder="email1@example.com, email2@example.com"
                        value={(selectedNode.data.config?.attendeeEmails || []).join(", ")}
                        onChange={(e) => {
                          const emailsText = e.target.value;
                          const emails = emailsText
                            .split(",")
                            .map((email) => email.trim())
                            .filter((email) => email.length > 0);
                          updateNodeConfig({ attendeeEmails: emails.length > 0 ? emails : undefined });
                        }}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Add email addresses (comma-separated) to include as meeting attendees
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="waitForResponseAppointment"
                        checked={
                          selectedNode.data.config?.waitForResponse ?? true
                        }
                        onCheckedChange={(checked) =>
                          updateNodeConfig({ waitForResponse: !!checked })
                        }
                        data-testid="checkbox-wait-for-response"
                      />
                      <Label
                        htmlFor="waitForResponseAppointment"
                        className="text-sm font-normal cursor-pointer"
                      >
                        {t("flows.nodeConfig.waitForResponse")}
                      </Label>
                    </div>
                  </div>
                )}

                {/* End Node */}
                {selectedNode.data.type === "end" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="endMessage">
                        {t("flows.nodeConfig.endMessage")}
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                        onClick={() => {
                          setGeneratingField("endMessage");
                          generateContentMutation.mutate({
                            nodeType: "end",
                            fieldType: "endMessage",
                            context: selectedNode.data.label || "goodbye message",
                          });
                        }}
                        disabled={generatingField === "endMessage"}
                      >
                        {generatingField === "endMessage" ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5 mr-1" />
                        )}
                        AI Assist
                      </Button>
                    </div>
                    <Textarea
                      id="endMessage"
                      value={selectedNode.data.config?.endMessage || ""}
                      onChange={(e) =>
                        updateNodeConfig({ endMessage: e.target.value })
                      }
                      placeholder={t("flows.nodeConfig.endMessagePlaceholder")}
                      data-testid="input-end-message"
                    />
                  </div>
                )}

                {/* State Machine Node */}
                {selectedNode.data.type === "stateMachine" && (
                  <div className="space-y-4">
                    {/* Description */}
                    <div>
                      <Label htmlFor="smDescription">Description</Label>
                      <Textarea
                        id="smDescription"
                        value={selectedNode.data.config?.description || ""}
                        onChange={(e) =>
                          updateNodeConfig({ description: e.target.value })
                        }
                        placeholder="Describe what this state machine does..."
                        className="mt-1"
                      />
                    </div>

                    {/* States Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <GitBranch className="w-4 h-4 text-purple-500" />
                          States
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7"
                          onClick={() => {
                            const newState = {
                              id: `state-${Date.now()}`,
                              name: `State ${(selectedNode.data.config?.states?.length || 0) + 1}`,
                              type: "message" as const,
                              message: "",
                            };
                            const currentStates = selectedNode.data.config?.states || [];
                            updateNodeConfig({
                              states: [...currentStates, newState],
                              entryStateId: currentStates.length === 0 ? newState.id : selectedNode.data.config?.entryStateId,
                            });
                          }}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add State
                        </Button>
                      </div>

                      {/* State List */}
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {(selectedNode.data.config?.states || []).map((state: any, index: number) => (
                          <div
                            key={state.id}
                            className={`p-2 rounded border ${
                              state.id === selectedNode.data.config?.entryStateId
                                ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                                : 'border-border'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Input
                                value={state.name}
                                onChange={(e) => {
                                  const newStates = [...(selectedNode.data.config?.states || [])];
                                  newStates[index] = { ...newStates[index], name: e.target.value };
                                  updateNodeConfig({ states: newStates });
                                }}
                                className="h-7 text-sm flex-1"
                                placeholder="State name"
                              />
                              <Select
                                value={state.type}
                                onValueChange={(value) => {
                                  const newStates = [...(selectedNode.data.config?.states || [])];
                                  newStates[index] = { ...newStates[index], type: value };
                                  updateNodeConfig({ states: newStates });
                                }}
                              >
                                <SelectTrigger className="h-7 w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="message">Message</SelectItem>
                                  <SelectItem value="question">Question</SelectItem>
                                  <SelectItem value="action">Action</SelectItem>
                                  <SelectItem value="decision">Decision</SelectItem>
                                </SelectContent>
                              </Select>
                              {state.id !== selectedNode.data.config?.entryStateId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-green-600"
                                  onClick={() => updateNodeConfig({ entryStateId: state.id })}
                                  title="Set as entry state"
                                >
                                  <Play className="w-3 h-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500"
                                onClick={() => {
                                  const newStates = (selectedNode.data.config?.states || []).filter(
                                    (s: any) => s.id !== state.id
                                  );
                                  updateNodeConfig({ states: newStates });
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            {(state.type === "message" || state.type === "question") && (
                              <Textarea
                                value={state.message || ""}
                                onChange={(e) => {
                                  const newStates = [...(selectedNode.data.config?.states || [])];
                                  newStates[index] = { ...newStates[index], message: e.target.value };
                                  updateNodeConfig({ states: newStates });
                                }}
                                className="mt-2 text-xs"
                                placeholder={state.type === "question" ? "Enter question..." : "Enter message..."}
                                rows={2}
                              />
                            )}
                            {state.type === "question" && (
                              <Input
                                value={state.variableName || ""}
                                onChange={(e) => {
                                  const newStates = [...(selectedNode.data.config?.states || [])];
                                  newStates[index] = { ...newStates[index], variableName: e.target.value };
                                  updateNodeConfig({ states: newStates });
                                }}
                                className="mt-1 h-7 text-xs"
                                placeholder="Variable name (e.g., customer_name)"
                              />
                            )}
                          </div>
                        ))}
                        {(!selectedNode.data.config?.states || selectedNode.data.config.states.length === 0) && (
                          <p className="text-xs text-muted-foreground text-center py-4">
                            No states defined. Click "Add State" to create one.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Transitions Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium">Transitions</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7"
                          onClick={() => {
                            const states = selectedNode.data.config?.states || [];
                            if (states.length < 2) return;
                            const newTransition = {
                              id: `trans-${Date.now()}`,
                              fromStateId: states[0]?.id || "",
                              toStateId: states[1]?.id || states[0]?.id || "",
                              condition: { type: "always" as const, value: "" },
                              label: "",
                            };
                            const currentTransitions = selectedNode.data.config?.transitions || [];
                            updateNodeConfig({ transitions: [...currentTransitions, newTransition] });
                          }}
                          disabled={(selectedNode.data.config?.states?.length || 0) < 2}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add Transition
                        </Button>
                      </div>

                      <div className="space-y-2 max-h-36 overflow-y-auto">
                        {(selectedNode.data.config?.transitions || []).map((transition: any, index: number) => (
                          <div key={transition.id} className="p-2 rounded border border-border text-xs">
                            <div className="flex items-center gap-1">
                              <Select
                                value={transition.fromStateId}
                                onValueChange={(value) => {
                                  const newTransitions = [...(selectedNode.data.config?.transitions || [])];
                                  newTransitions[index] = { ...newTransitions[index], fromStateId: value };
                                  updateNodeConfig({ transitions: newTransitions });
                                }}
                              >
                                <SelectTrigger className="h-6 text-xs flex-1">
                                  <SelectValue placeholder="From" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(selectedNode.data.config?.states || []).map((s: any) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-muted-foreground">→</span>
                              <Select
                                value={transition.toStateId}
                                onValueChange={(value) => {
                                  const newTransitions = [...(selectedNode.data.config?.transitions || [])];
                                  newTransitions[index] = { ...newTransitions[index], toStateId: value };
                                  updateNodeConfig({ transitions: newTransitions });
                                }}
                              >
                                <SelectTrigger className="h-6 text-xs flex-1">
                                  <SelectValue placeholder="To" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(selectedNode.data.config?.states || []).map((s: any) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500"
                                onClick={() => {
                                  const newTransitions = (selectedNode.data.config?.transitions || []).filter(
                                    (t: any) => t.id !== transition.id
                                  );
                                  updateNodeConfig({ transitions: newTransitions });
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                            <div className="mt-1 flex items-center gap-1">
                              <Select
                                value={transition.condition?.type || "always"}
                                onValueChange={(value) => {
                                  const newTransitions = [...(selectedNode.data.config?.transitions || [])];
                                  newTransitions[index] = {
                                    ...newTransitions[index],
                                    condition: { ...newTransitions[index].condition, type: value },
                                  };
                                  updateNodeConfig({ transitions: newTransitions });
                                }}
                              >
                                <SelectTrigger className="h-6 text-xs w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="always">Always</SelectItem>
                                  <SelectItem value="llm">LLM</SelectItem>
                                  <SelectItem value="intent">Intent</SelectItem>
                                  <SelectItem value="variable">Variable</SelectItem>
                                </SelectContent>
                              </Select>
                              {transition.condition?.type !== "always" && (
                                <Input
                                  value={transition.condition?.value || ""}
                                  onChange={(e) => {
                                    const newTransitions = [...(selectedNode.data.config?.transitions || [])];
                                    newTransitions[index] = {
                                      ...newTransitions[index],
                                      condition: { ...newTransitions[index].condition, value: e.target.value },
                                    };
                                    updateNodeConfig({ transitions: newTransitions });
                                  }}
                                  className="h-6 text-xs flex-1"
                                  placeholder={
                                    transition.condition?.type === "llm" ? "Condition description..."
                                    : transition.condition?.type === "intent" ? "Intent name..."
                                    : "Variable check..."
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                        {(!selectedNode.data.config?.transitions || selectedNode.data.config.transitions.length === 0) && (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            {(selectedNode.data.config?.states?.length || 0) < 2
                              ? "Add at least 2 states first"
                              : "No transitions defined"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Exit Points Section */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <StopCircle className="w-4 h-4 text-red-500" />
                          Exit Points
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7"
                          onClick={() => {
                            const colors = ['#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
                            const exitPoints = selectedNode.data.config?.exitPoints || [];
                            const newExitPoint = {
                              id: `exit-${Date.now()}`,
                              name: `Exit ${exitPoints.length + 1}`,
                              color: colors[exitPoints.length % colors.length],
                              description: "",
                            };
                            updateNodeConfig({ exitPoints: [...exitPoints, newExitPoint] });
                          }}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add Exit
                        </Button>
                      </div>

                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {(selectedNode.data.config?.exitPoints || []).map((exitPoint: any, index: number) => (
                          <div key={exitPoint.id} className="flex items-center gap-2 p-2 rounded border border-border">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: exitPoint.color }}
                            />
                            <Input
                              value={exitPoint.name}
                              onChange={(e) => {
                                const newExitPoints = [...(selectedNode.data.config?.exitPoints || [])];
                                newExitPoints[index] = { ...newExitPoints[index], name: e.target.value };
                                updateNodeConfig({ exitPoints: newExitPoints });
                              }}
                              className="h-7 text-sm flex-1"
                              placeholder="Exit name"
                            />
                            <Input
                              type="color"
                              value={exitPoint.color || "#8b5cf6"}
                              onChange={(e) => {
                                const newExitPoints = [...(selectedNode.data.config?.exitPoints || [])];
                                newExitPoints[index] = { ...newExitPoints[index], color: e.target.value };
                                updateNodeConfig({ exitPoints: newExitPoints });
                              }}
                              className="h-7 w-10 p-0.5 cursor-pointer"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500"
                              onClick={() => {
                                const newExitPoints = (selectedNode.data.config?.exitPoints || []).filter(
                                  (ep: any) => ep.id !== exitPoint.id
                                );
                                updateNodeConfig({ exitPoints: newExitPoints });
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        {(!selectedNode.data.config?.exitPoints || selectedNode.data.config.exitPoints.length === 0) && (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            No exit points. Add exit points to route to other nodes.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Advanced Options */}
                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium mb-2 block">Advanced Options</Label>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Max Duration (seconds)</Label>
                          <Input
                            type="number"
                            value={selectedNode.data.config?.maxDurationSeconds || 120}
                            onChange={(e) =>
                              updateNodeConfig({ maxDurationSeconds: parseInt(e.target.value) || 120 })
                            }
                            className="h-7 mt-1"
                            min={30}
                            max={600}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Fallback Exit Point</Label>
                          <Select
                            value={selectedNode.data.config?.fallbackExitPointId || ""}
                            onValueChange={(value) => updateNodeConfig({ fallbackExitPointId: value })}
                          >
                            <SelectTrigger className="h-7 mt-1">
                              <SelectValue placeholder="Select fallback exit" />
                            </SelectTrigger>
                            <SelectContent>
                              {(selectedNode.data.config?.exitPoints || []).map((ep: any) => (
                                <SelectItem key={ep.id} value={ep.id}>{ep.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Insertion Suggestion Dialog */}
      <Dialog open={showInsertionDialog} onOpenChange={setShowInsertionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-500" />
              Add Nodes to Flow
            </DialogTitle>
            <DialogDescription>
              I've analyzed your flow and have a suggestion for where to add the new nodes.
            </DialogDescription>
          </DialogHeader>

          {insertionSuggestion && (
            <div className="space-y-4">
              <Alert className="bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-800 dark:text-purple-200">
                  <p className="font-medium mb-1">Suggested insertion point:</p>
                  <p className="text-sm">After "{insertionSuggestion.insertAfterNodeLabel}"</p>
                  <p className="text-xs mt-2 text-purple-600 dark:text-purple-400">
                    {insertionSuggestion.reason}
                  </p>
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Or choose a different node:</Label>
                <Select
                  value={insertionSuggestion.insertAfterNodeId}
                  onValueChange={handleInsertionChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select node" />
                  </SelectTrigger>
                  <SelectContent>
                    {nodes.map((node) => (
                      <SelectItem key={node.id} value={node.id}>
                        {node.data?.label || node.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">What will be added:</p>
                <p className="text-sm font-medium">{pendingPrompt}</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowInsertionDialog(false);
                setInsertionSuggestion(null);
                setPendingPrompt("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInsertionConfirm}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
              disabled={isGeneratingNodes}
            >
              {isGeneratingNodes ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Add Nodes Here
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Flow Dialog */}
      {flowId && (
        <TestFlowDialog
          open={testDialogOpen}
          onOpenChange={setTestDialogOpen}
          flowId={flowId}
          flowName={flowName}
        />
      )}

      {/* Help Drawer */}
      <FlowBuilderHelpDrawer
        open={helpDrawerOpen}
        onOpenChange={setHelpDrawerOpen}
      />

      {/* The flow read end to end as a script. Fed from live canvas state, not
          the saved flow, so it shows what you are about to ship. Selecting a
          step closes the panel and opens that node's config - the script is for
          finding the line, the canvas is for changing it. */}
      <FlowScriptPanel
        open={scriptOpen}
        onOpenChange={setScriptOpen}
        flowName={flowName}
        nodes={nodes}
        edges={edges}
        onSelectNode={(nodeId) => {
          const node = nodes.find((n) => n.id === nodeId);
          if (!node) return;
          setScriptOpen(false);
          setSelectedNode(node as FlowNode);
        }}
      />

      {/* Screenshot Vision Edit Dialog */}
      <Dialog open={screenshotDialogOpen} onOpenChange={(open) => {
        setScreenshotDialogOpen(open);
        if (!open) {
          setScreenshotImage(null);
          setScreenshotCommand("");
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-500" />
              Screenshot-Based Flow Edit
            </DialogTitle>
            <DialogDescription>
              Describe what you want to change. For example: "delete the green node" or "remove the connection between greeting and question"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Screenshot Preview */}
            {screenshotImage && (
              <div className="relative border rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={screenshotImage}
                  alt="Flow screenshot"
                  className="max-h-64 w-full object-contain"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 bg-white/80 dark:bg-gray-900/80"
                  onClick={() => screenshotInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Change
                </Button>
              </div>
            )}

            {/* Command Input */}
            <div className="space-y-2">
              <Label htmlFor="screenshot-command">What would you like to do?</Label>
              <Textarea
                id="screenshot-command"
                placeholder="e.g., 'Delete the appointment node' or 'Remove the connection from the first node to the second'"
                value={screenshotCommand}
                onChange={(e) => setScreenshotCommand(e.target.value)}
                className="min-h-[80px]"
                disabled={isProcessingScreenshot}
              />
            </div>

            {/* Example Commands */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">Example commands:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>"Delete the node that says 'Welcome'"</li>
                <li>"Remove the connection between greeting and question"</li>
                <li>"Delete the red/green/blue node"</li>
                <li>"Remove the last node"</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setScreenshotDialogOpen(false);
                setScreenshotImage(null);
                setScreenshotCommand("");
              }}
              disabled={isProcessingScreenshot}
            >
              Cancel
            </Button>
            <Button
              onClick={processScreenshotEdit}
              disabled={!screenshotImage || !screenshotCommand.trim() || isProcessingScreenshot}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isProcessingScreenshot ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Apply Edit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
